import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { supabase } from '../lib/supabase';
import { useOwner } from '../hooks/useOwner';
import { cropStyle } from '../utils/photoCrop';
import { isNative } from '../utils/native';
import { formatCurrency, formatDate, formatMileage } from '../utils/dateUtils';
import { MileageInput, MileageMismatchNotice } from '../components/common/MileageInput';
import { Modal } from '../components/common/Modal';
import {
  TrendingUp, TrendingDown, Minus, Plus, Trash2,
  Car, ExternalLink, Brain, BarChart3, Save,
  ChevronDown, ChevronUp, Loader, Search, Link,
  Sparkles, Edit2, DollarSign, Star, FileText, Lock, X
} from 'lucide-react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, RadarChart, Radar,
  PolarGrid, PolarAngleAxis, PolarRadiusAxis,
  ScatterChart, Scatter
} from 'recharts';
import { format, differenceInMonths, parseISO } from 'date-fns';
import toast from 'react-hot-toast';
import { usePlan } from '../hooks/usePlan';
import { PhoneHero } from '../components/common/PhoneHero';
import { StickyBar } from '../components/common/StickyBar';
import { useNavigate } from 'react-router-dom';

const GRADIENTS = [
  ['#4f8ef7', '#7c3aed'], ['#22c55e', '#06b6d4'],
  ['#f97316', '#ef4444'], ['#eab308', '#f97316'],
  ['#a855f7', '#ec4899'], ['#06b6d4', '#4f8ef7'],
];


const CONDITION_FACTORS  = { 'Poor': 0.70, 'Fair': 0.85, 'Good': 1.00, 'Very Good': 1.10, 'Excellent': 1.20, 'Concours': 1.35 };
const MILEAGE_FACTORS    = { 'Very High': 0.80, 'Average': 1.00, 'Low': 1.10, 'Extremely Low': 1.20 };
const ORIGINALITY_FACTORS= { 'Heavily Modified': 0.80, 'Some Modifications': 0.90, 'Mostly Original': 1.05, 'Fully Original': 1.15 };
const SERVICE_FACTORS    = { 'No History': 0.85, 'Partial History': 0.95, 'Good History': 1.05, 'Full Documented History': 1.15 };
const RARITY_FACTORS     = { 'Common': 1.00, 'Desirable': 1.10, 'Rare': 1.20, 'Very Rare': 1.35 };
const DEMAND_FACTORS     = { 'Falling': 0.90, 'Stable': 1.00, 'Increasing': 1.10, 'Strong Demand': 1.25 };

const CATEGORIES = [
  'JDM', 'Air-cooled Porsche', 'Analog Ferrari', 'Classic British',
  'Classic American', 'Classic Italian', 'Classic German', 'Classic French',
  'Classic SUV', 'Youngtimer', '80s/90s Nostalgia', 'Supercar', 'Other'
];
const SOURCE_WEBSITES = [
  'Bring a Trailer', 'Cars & Bids', "RM Sotheby's", 'Bonhams',
  'Collecting Cars', 'Classic.com', 'eBay Motors', 'AutoScout24',
  'mobile.de', 'Bazaraki', 'Facebook Marketplace', 'Dealer', 'Private Sale', 'Other'
];
const CURRENCIES = ['EUR', 'USD', 'GBP', 'CHF', 'AED', 'JPY', 'Other'];

// ── Supply / Scarcity Multiplier ────────────────────────────────────────────
// "Estimated Remaining" is a stronger scarcity signal than "Total Units Produced"
// alone — a car built in huge numbers but almost all scrapped is rarer TODAY than
// a low-production car that nearly all survived — so remaining units wins when
// known, and total production is only the fallback when remaining is blank.
// Tiers per the owner's own spec: ≤50 left = 1.5x, 51–100 = 1.3x, tapering down
// from there. This multiplier is applied directly to both the Collector Score's
// Rarity category AND the estimated value (see calcCollectorScore / calcEstimatedValue).
const SUPPLY_TIERS_REMAINING  = [{ max: 50, mult: 1.50 }, { max: 100, mult: 1.30 }, { max: 250, mult: 1.15 }, { max: 1000, mult: 1.05 }];
const SUPPLY_TIERS_PRODUCTION = [{ max: 500, mult: 1.15 }, { max: 5000, mult: 1.05 }, { max: 50000, mult: 1.00 }];
const calcSupplyMultiplier = (v) => {
  const remaining = v?.remaining_units != null && v.remaining_units !== '' ? Number(v.remaining_units) : null;
  if (remaining != null && remaining >= 0) {
    const tier = SUPPLY_TIERS_REMAINING.find(t => remaining <= t.max);
    return { multiplier: tier ? tier.mult : 1.00, basis: 'remaining_units', value: remaining };
  }
  const produced = v?.total_production != null && v.total_production !== '' ? Number(v.total_production) : null;
  if (produced != null && produced >= 0) {
    const tier = SUPPLY_TIERS_PRODUCTION.find(t => produced <= t.max);
    return { multiplier: tier ? tier.mult : 0.98, basis: 'total_production', value: produced };
  }
  return { multiplier: 1.00, basis: 'none', value: null };
};

// ── Special Edition Multiplier ──────────────────────────────────────────────
// Toggling "Special / Limited Edition" now always carries real, visible weight.
// The owner can enter their own multiplier (a numbered 50-off Anniversary Edition
// is worth more than an unnumbered "Sport" trim called special); left blank, it
// defaults to 1.5x — the minimum the owner asked for.
const DEFAULT_SPECIAL_EDITION_MULTIPLIER = 1.5;
const calcSpecialEditionMultiplier = (v) => {
  if (!v?.special_edition) return 1.0;
  const custom = v.special_edition_multiplier != null && v.special_edition_multiplier !== ''
    ? Number(v.special_edition_multiplier) : null;
  return custom && custom > 0 ? custom : DEFAULT_SPECIAL_EDITION_MULTIPLIER;
};

// ── Collector Score ───────────────────────────────────────────────────────────
// Collector score. Categories with no data recorded count as NEUTRAL (50%) rather than
// zero, so missing information lowers confidence instead of pretending the car is poor.
// Exported so the Vehicle Passport shows exactly the same score as this page.
export const calcCollectorScore = (v) => {
  if (!v) return { total: 0, completeness: 0, breakdown: {} };

  const cats = [];
  const add = (name, max, recorded, score) => cats.push({ name, max, recorded, score });

  // Originality (25)
  const origItems = [v.original_paint, v.matching_numbers, v.original_interior, v.oem_parts, v.factory_wheels, v.original_radio, v.no_modifications];
  const origRecorded = origItems.some(Boolean);
  add('Originality', 25, origRecorded, Math.round((origItems.filter(Boolean).length / 7) * 25));

  // Condition (25)
  const condMap = { Concours: 25, Excellent: 20, Good: 15, Fair: 8, Poor: 3 };
  add('Condition', 25, !!v.condition_grade, condMap[v.condition_grade] || 0);

  // Service history (15)
  let serviceScore = v.service_history_status === 'Complete' ? 15 : v.service_history_status === 'Partial' ? 8 : 0;
  if (v.specialist_servicing) serviceScore = Math.min(serviceScore + 2, 15);
  if (v.original_manuals)     serviceScore = Math.min(serviceScore + 2, 15);
  add('Service History', 15, !!v.service_history_status || v.specialist_servicing || v.original_manuals, serviceScore);

  // Rarity (20) — driven by the Supply multiplier (remaining units, or total
  // production as a fallback) and the Special Edition multiplier. Each 0.1 of
  // multiplier above 1.0 is worth 2 rarity points, so the ≤50-remaining tier
  // (1.5x) or a 1.5x special edition alone swings this category from neutral
  // (10/20) to near its maximum on its own.
  const supply = calcSupplyMultiplier(v);
  const specialMult = calcSpecialEditionMultiplier(v);
  let rarityScore = 10 + (supply.multiplier - 1) * 20 + (specialMult - 1) * 20;
  const colorBonus = { 'Extremely Rare': 4, 'Rare': 2, 'Uncommon': 1, 'Common': 0 };
  rarityScore = Math.max(0, Math.min(rarityScore + (colorBonus[v.factory_color_rarity] || 0), 20));
  rarityScore = Math.round(rarityScore);
  add('Rarity', 20, !!(v.total_production || v.remaining_units || v.special_edition || v.factory_color_rarity), rarityScore);

  // Provenance (10)
  let provScore = 0;
  if (v.single_owner)      provScore += 3;
  if (v.matching_vin_docs) provScore += 3;
  if (v.museum_history)    provScore += 2;
  if (v.celebrity_owned)   provScore += 2;
  if (v.vin)               provScore += 1;
  if (v.factory_order_number || v.build_date) provScore += 1;
  provScore = Math.min(provScore, 10);
  add('Provenance', 10, !!(v.single_owner || v.matching_vin_docs || v.museum_history || v.celebrity_owned || v.vin || v.factory_order_number || v.build_date), provScore);

  // Restoration (5) — a documented, professional or frame-off job scores highest;
  // "Restoration Docs" (major_restoration_docs) adds a point on top of any restored
  // status, since paperwork is what makes a restoration claim credible.
  let restScore = 0;
  if (v.restoration_status === 'Original Survivor') restScore = 5;
  else if (v.restoration_status === 'Restored' && v.frame_off_restoration) restScore = 5;
  else if (v.restoration_status === 'Restored' && v.professional_restoration) restScore = 4;
  else if (v.restoration_status === 'Restored') restScore = 3;
  else if (v.restoration_status === 'Partially Restored') restScore = 2;
  if (v.restoration_status && v.restoration_status !== 'Original Survivor' && v.major_restoration_docs) {
    restScore = Math.min(restScore + 1, 5);
  }
  add('Restoration', 5, !!v.restoration_status, restScore);

  // Colour premium (5)
  const desirabilityMap = { 'Highly Desirable': 5, 'Desirable': 3, 'Neutral': 2, 'Less Desirable': 0 };
  add('Colour Premium', 5, !!v.colour_desirability, desirabilityMap[v.colour_desirability] ?? 0);

  // Weighted total — unrecorded categories contribute a neutral 50%
  const maxTotal = cats.reduce((s, c) => s + c.max, 0);
  const earned   = cats.reduce((s, c) => s + (c.recorded ? c.score : c.max * 0.5), 0);

  let modMultiplier = 1.0;
  if (v.modification_impact === 'Value Positive')   modMultiplier = 1.05;
  if (v.modification_impact === 'Value Negative')   modMultiplier = 0.92;
  if (v.modification_impact === 'Heavily Modified') modMultiplier = 0.80;

  const total = Math.min(Math.max(Math.round((earned / maxTotal) * 100 * modMultiplier), 0), 100);
  const completeness = Math.round((cats.filter(c => c.recorded).length / cats.length) * 100);

  const breakdown = {};
  cats.forEach(c => { breakdown[c.name] = { score: c.recorded ? c.score : Math.round(c.max * 0.5), max: c.max, recorded: c.recorded }; });
  breakdown['Modifications'] = { score: v.modification_impact === 'Value Positive' ? 5 : v.modification_impact === 'Value Negative' ? 2 : v.modification_impact === 'Heavily Modified' ? 0 : 3, max: 5, recorded: !!v.modification_impact };

  return { total, completeness, breakdown };
};
// Relevance scoring — how well a comparable matches the selected vehicle's spec.
const cmpNorm = (s) => (s == null ? '' : String(s)).trim().toLowerCase();

// ═══════════════════════════════════════════════════════════════════════════
// MATCH TUNING — adjust these to change how comparables are ranked.
// Weights are points out of 100. They should sum to 100.
// ═══════════════════════════════════════════════════════════════════════════
const MATCH_WEIGHTS = {
  identity:  33,   // model family + variant/body words (the anchor)
  engine:    24,   // displacement
  year:      18,   // proximity
  colour:    12,   // exterior colour — matters more for classics than modern cars
  mileage:   10,   // proximity, unit-normalised
  condition:  3,   // rarely recorded on comps, so kept small
};

// How close counts as "same" before scoring starts to fall away.
const MATCH_TUNING = {
  mileageCloseRatio:  0.15,   // within 15% = full marks
  mileageOkRatio:     0.35,
  engineTolerance:    0.2,    // litres; within this scores partial rather than zero
  neutralFraction:    0.6,    // unknown-on-either-side scores this fraction of the weight
};

// Words that describe BODY/VARIANT and genuinely separate different cars.
// A contradiction between two of these excludes a comparable outright.
const BODY_GROUPS = [
  ['roadster', 'convertible', 'cabriolet', 'cabrio', 'spider', 'spyder', 'volante', 'dhc', 'ots'],
  ['coupe', 'fhc', 'fixedhead', 'hardtop', 'berlinetta'],
  ['targa'],
  ['saloon', 'sedan', 'berlina'],
  ['estate', 'wagon', 'touring', 'shooting'],
];

// Words that mark a distinct VARIANT within a model family. Present on one side and
// contradicted on the other = different car (930 Turbo vs 911 SC).
const VARIANT_MARKERS = new Set([
  'turbo', 'sc', 'carrera', 'targa', 'gt', 'gts', 'gt3', 'gt2', 'rs', 'rsr', 's',
  'vantage', 'volante', 'virage', 'db7', 'db9', 'superleggera',
  'vogue', 'autobiography', 'defender', 'discovery', 'westminster',
  'quattro', 'cosworth', 'integrale', 'evoluzione', 'abarth',
  'amg', 'm3', 'm5', 'm6', 'csl', 'alpina',
  'stingray', 'z06', 'zr1', 'boss', 'mach', 'shelby', 'cobra',
  'lusso', 'spider', 'roadster', 'coupe', 'fhc', 'dhc', 'saloon', 'estate',
  // Confirmed from Jeep Cherokee/Grand Cherokee comparables data — these trim
  // words were previously absent from this list, so isStrictMatch() treated a
  // Cherokee Sport, Limited, Laredo, SRT, Trackhawk etc. as all equally valid
  // comparables for each other (the trim was simply never checked). Purely
  // additive: this can only make matching STRICTER, never looser, for any make.
  'sport', 'limited', 'laredo', 'overland', 'trackhawk', 'srt', 'wagoneer',
  'briarwood', 'chief', 'classic', 'country', 'pioneer', 'se',
]);

// Tokens too generic to discriminate — ignored when comparing identity.
const NOISE_TOKENS = new Set([
  'the', 'and', 'with', 'series', 'mk', 'mark', 'model', 'base', 'standard',
  'edition', 'litre', 'liter', 'speed', 'manual', 'automatic', 'auto', 'lhd', 'rhd',
  'v8', 'v12', 'v6', 'i6', 'straight', 'six', 'flat', 'inline', 'na', 'n', 'a',
]);

const tokenize = (s) => (s == null ? '' : String(s))
  .toLowerCase()
  .replace(/\(.*?\)/g, ' ')
  .replace(/[^a-z0-9.+]+/g, ' ')
  .split(' ')
  .filter(t => t && !NOISE_TOKENS.has(t));

// Everything a record says about WHAT the car is, regardless of which field holds it.
// This is the whole point: source listings scatter the same facts across model,
// variant and engine columns, so comparing field-to-field misses real matches.
const vehicleIdentityTokens = (v) => tokenize(
  `${v.model || ''} ${v.variant || ''} ${v.generation || ''} ${v.body_style || ''}`);

// The comp's identity is spread across FOUR columns, not two. Body style and
// generation live in the normalised columns — an E-Type Series 3 Roadster has
// model "E-Type", variant NULL, and the rest in normalized_generation /
// normalized_body_style. Reading only model+variant meant a vehicle stating
// "Roadster" found that word on no comp at all, and the distinctive-token rule
// excluded every one of the 437 genuine matches.
const compIdentityTokens    = (c) => tokenize(
  `${c.model || ''} ${c.variant || ''} ${c.normalized_generation || ''} ${c.normalized_body_style || ''}`);

const bodyGroupOf = (tokens) => {
  for (let i = 0; i < BODY_GROUPS.length; i++) {
    if (tokens.some(t => BODY_GROUPS[i].includes(t))) return i;
  }
  return null;
};

const CONDITION_RANK = { poor: 1, fair: 2, good: 3, excellent: 4, concours: 5 };

// A token is DISTINCTIVE if it narrows the car down: a trim word (Touring, CS),
// a spec (16v, 4.2), or a body number (90, 110). These are what separate an
// M5 Touring from an M5 CS, a Delta 16V from an 8V, a Defender 90 from a 110.
//
// The previous rule only excluded on a CURATED word list, so anything unknown
// passed silently — which is how an X7 SUV and an i4 Gran Coupé ended up as
// comps for an M5 Touring, and 8V Deltas for a 16V.
const isDistinctive = (t) =>
  VARIANT_MARKERS.has(t) ||
  BODY_GROUPS.some(g => g.includes(t)) ||   // touring/estate/coupe/roadster: a body
                                            // word the comp lacks means a different car
  /^\d{2,3}$/.test(t) ||              // 90, 110, 130, 993
  /^\d+v$/.test(t) ||                 // 8v, 16v, 24v
  /^\d\.\d$/.test(t) ||               // 4.2, 5.3
  /^(evo|evoluzione|sc|cs|csl|gts?|rs|ti|td|tdi|sdv\d|hse|xs)$/.test(t) ||
  (t.length >= 3 && /^[a-z]+\d+[a-z]*$/.test(t));   // f90, g07, m50i, sdv8

// ── Hard exclusion ─────────────────────────────────────────────────────────
// A comp is excluded when it CONTRADICTS the vehicle — including on words we
// don't have in any curated list. Missing data still never excludes.
const isStrictMatch = (v, c) => {
  const vTok = vehicleIdentityTokens(v);
  const cTok = compIdentityTokens(c);
  const cAll = cTok.join(' ');

  // 1. Body style contradiction (a roadster is not a coupe).
  const vBody = bodyGroupOf(vTok), cBody = bodyGroupOf(cTok);
  if (vBody !== null && cBody !== null && vBody !== cBody) return false;

  // 2. Every DISTINCTIVE word the vehicle states must appear on the comp.
  //    "M5 Touring" vs "M5 CS": the comp has no `touring`, so it's a different car.
  //    "Delta 16V" vs "Delta 8V": no `16v` on the comp.
  //    "Defender 90" vs "Defender 110": no `90`.
  const vDistinct = vTok.filter(isDistinctive);
  for (const t of vDistinct) {
    if (!cTok.includes(t) && !cAll.includes(t)) return false;
  }

  // 3. Conversely, a distinctive word on the COMP that the vehicle contradicts.
  //    Only applied within the same "kind" of token, so a comp carrying extra
  //    detail the vehicle simply doesn't record isn't punished.
  const kindOf = (t) => /^\d+v$/.test(t) ? 'valves'
                      : /^\d{2,3}$/.test(t) ? 'number'
                      : /^\d\.\d$/.test(t) ? 'litres' : null;
  // A SET per kind, not a single value: a vehicle can legitimately carry several
  // numbers — a 911 of generation 930 has both — and matching either is a match,
  // not a conflict. Storing one overwrote the other and excluded genuine comps.
  const vKinds = {};
  vTok.forEach(t => { const k = kindOf(t); if (k) (vKinds[k] = vKinds[k] || new Set()).add(t); });
  for (const t of cTok.filter(isDistinctive)) {
    const k = kindOf(t);
    if (k && vKinds[k] && vKinds[k].size && !vKinds[k].has(t)) return false;
  }

  // 4. Engine displacement contradiction — skipped when a shared variant word
  //    already confirms the same car (a 4.4 and 5.0 Vogue are engine options).
  const vMarkers = vTok.filter(t => VARIANT_MARKERS.has(t));
  const cMarkers = cTok.filter(t => VARIANT_MARKERS.has(t));
  const sameVariantConfirmed = vMarkers.length && cMarkers.length &&
    vMarkers.some(t => cMarkers.includes(t));
  const vd = displacement(v.engine_size);
  if (vd && !sameVariantConfirmed) {
    const cd = compDisplacement(c);
    if (cd && cd !== vd && Math.abs(parseFloat(cd) - parseFloat(vd)) > MATCH_TUNING.engineTolerance) return false;
  }
  return true;};

// ── Scoring: fixed 100-point scale so percentages compare car-to-car ───────
const scoreComparable = (v, c) => {
  if (!v) return { score: 0, max: 100, pct: 0, fields: [] };
  const W = MATCH_WEIGHTS, T = MATCH_TUNING;
  const neutral = (w) => w * T.neutralFraction;
  let score = 0; const fields = [];

  // Identity — token overlap across the COMBINED text of both records.
  const vTok = vehicleIdentityTokens(v);
  const cTok = compIdentityTokens(c);
  if (vTok.length && cTok.length) {
    const cAll = cTok.join(' ');
    let hit = 0, total = 0;
    vTok.forEach(t => {
      const weight = VARIANT_MARKERS.has(t) ? 3 : 1;   // distinctive words count more
      total += weight;
      if (cTok.includes(t) || cAll.includes(t)) hit += weight;
    });
    const ratio = total ? hit / total : 0;
    score += W.identity * ratio;
    if (ratio >= 0.6) fields.push('identity');
  } else {
    score += neutral(W.identity);
  }

  // Engine displacement
  const vd = displacement(v.engine_size), cd = compDisplacement(c);
  if (vd && cd) {
    if (vd === cd) { score += W.engine; fields.push('engine'); }
    else {
      const diff = Math.abs(parseFloat(vd) - parseFloat(cd));
      score += diff <= T.engineTolerance ? W.engine * 0.5 : 0;
    }
  } else {
    score += neutral(W.engine);
  }

  // Year proximity
  if (v.year && c.year) {
    const dy = Math.abs(Number(v.year) - Number(c.year));
    const f = dy === 0 ? 1 : dy <= 1 ? 0.9 : dy <= 2 ? 0.75 : dy <= 3 ? 0.6 : dy <= 5 ? 0.4 : dy <= 8 ? 0.2 : 0.05;
    score += W.year * f;
    if (dy <= 2) fields.push('year');
  } else {
    score += neutral(W.year);
  }

  // Exterior colour — weighted higher than for modern cars: on a classic, an
  // original or desirable colour is a real part of what a buyer is paying for.
  const vCol = cmpNorm(v.color), cCol = cmpNorm(c.exterior_colour);
  if (vCol && cCol) {
    if (vCol === cCol || vCol.includes(cCol) || cCol.includes(vCol)) {
      score += W.colour; fields.push('colour');
    } else {
      const a = vCol.split(' ').filter(Boolean), b = cCol.split(' ').filter(Boolean);
      if (a.some(w => w.length > 2 && b.includes(w))) { score += W.colour * 0.5; fields.push('colour'); }
    }
  } else {
    score += neutral(W.colour);
  }

  // Mileage proximity (comp normalised to km)
  if (v.mileage && c.mileage) {
    const cKm = String(c.mileage_unit).toLowerCase() === 'miles' ? Number(c.mileage) * 1.60934 : Number(c.mileage);
    const vKm = Number(v.mileage);
    if (vKm > 0) {
      const diff = Math.abs(vKm - cKm) / vKm;
      const f = diff <= T.mileageCloseRatio ? 1 : diff <= T.mileageOkRatio ? 0.7 : diff <= 0.6 ? 0.3 : 0.1;
      score += W.mileage * f;
      if (diff <= T.mileageOkRatio) fields.push('mileage');
    } else { score += neutral(W.mileage); }
  } else {
    score += neutral(W.mileage);
  }

  // Condition — comps rarely carry a grade, so this stays small
  const vCond = CONDITION_RANK[cmpNorm(v.condition_grade)];
  const cCond = CONDITION_RANK[cmpNorm(c.condition)];
  if (vCond && cCond) {
    const gap = Math.abs(vCond - cCond);
    score += W.condition * (gap === 0 ? 1 : gap === 1 ? 0.6 : gap === 2 ? 0.3 : 0);
    if (gap <= 1) fields.push('condition');
  } else {
    score += neutral(W.condition);
  }

  const pct = Math.max(0, Math.min(100, Math.round(score)));
  return { score, max: 100, pct, fields };};
const matchTier = (pct) => pct >= 85 ? { label: `${pct}% match`, color: '#22c55e' }
  : pct >= 65 ? { label: `${pct}% match`, color: '#4f8ef7' }
  : pct >= 45 ? { label: `${pct}% match`, color: '#eab308' }
  : { label: `${pct}% match`, color: '#9ca3af' };

// --- Currency normalization (approx current rates; refine to dated rates later) ---
const FX_TO_EUR = { EUR: 1, GBP: 1.18, USD: 0.92, AUD: 0.60, NZD: 0.56, CHF: 1.05, HKD: 0.118, CAD: 0.68, JPY: 0.0061, SEK: 0.088, DKK: 0.134, NOK: 0.086, ZAR: 0.050, AED: 0.25 };
const toEUR = (amount, currency) => {
  const a = Number(amount);
  if (!a || a <= 0) return null;
  const code = (currency || 'EUR').toUpperCase().replace(/[^A-Z]/g, '');
  const rate = FX_TO_EUR[code];
  return rate ? a * rate : null;
};
// --- Body style ---
const normBody = (s) => {
  const t = (s || '').toLowerCase();
  if (!t) return null;
  if (/(cabriolet|convertible|cabrio|roadster|spider|spyder|volante)/.test(t)) return 'convertible';
  if (/targa/.test(t)) return 'targa';
  return 'closed';
};
const isOpenText = (s) => /(cabriolet|convertible|cabrio|roadster|spider|spyder|volante)/.test((s || '').toLowerCase());
const isTargaText = (s) => /targa/.test((s || '').toLowerCase());
// ─────────────────────────────────────────────────────────────────────────────
// Comparable exclusion terms — checked against a comp's model + variant text.
//
//   UNIVERSAL: race/competition prep — never a fair comp for a road car, any marque.
//   PER_MAKE:  coachbuilders & aftermarket tuners, keyed by the OWNED car's make.
//
// A term only excludes a comp if the OWNED vehicle does NOT also carry it, so a
// RUF/Alpina owner still sees their own market while a standard owner has them removed.
// To support a new marque, add a lowercase key with its tuner terms. Be deliberate:
// marques with their own established market (RUF, Alpina) are worth excluding for
// standard owners but kept for their own owners — the vehicle-aware check handles both.
// ─────────────────────────────────────────────────────────────────────────────
const UNIVERSAL_EXCLUSIONS = ['ex-works', 'ex works', 'works entry', 'works rally', 'fia', 'competition', 'le mans', 'racing', ' race ', 'race car', 'rally', ' cup', 'gruppe', 'group b', 'safari', 'tribute', 'recreation', 'replica', 'kit car'];
const MAKE_EXCLUSIONS = {
  porsche:     ['rinspeed', 'gemballa', 'ruf', 'slantnose', 'slant nose', 'slant-nose', 'flachbau', 'strosek', 'koenig', 'kremer', 'dp motorsport'],
  mercedes:    ['brabus', 'amg hammer', 'lorinser', 'carlsson'],
  bmw:         ['alpina', 'hartge', 'ac schnitzer', 'hamann'],
  audi:        ['abt', 'mtm'],
  volkswagen:  ['abt'],
  ferrari:     ['zagato', 'straman', 'koenig'],
  lamborghini: ['koenig'],
  landrover:   ['overfinch', 'kahn', 'twisted'],
  jaguar:      ['lister', 'arden'],
  bentley:     ['mansory'],
  rollsroyce:  ['mansory'],
};
// Normalize a make to a map key: lowercase, drop punctuation/spaces, and fold common
// aliases so "Mercedes-Benz", "Mercedes Benz", "MERCEDES" all resolve to the same list.
const makeKey = (brand) => {
  const k = (brand || '').toLowerCase().replace(/[^a-z]/g, '');
  if (k.startsWith('mercedes')) return 'mercedes';
  if (k.startsWith('rolls')) return 'rollsroyce';
  if (k.startsWith('land') || k.startsWith('range')) return 'landrover';
  if (k.startsWith('vw') || k.startsWith('volkswagen')) return 'volkswagen';
  return k;
};
const isProvenanceCar = (c, vehicle) => {
  const cText = ` ${c.model || ''} ${c.variant || ''} `.toLowerCase();
  const vText = ` ${vehicle?.brand || ''} ${vehicle?.model || ''} ${vehicle?.variant || ''} `.toLowerCase();
  const terms = [...UNIVERSAL_EXCLUSIONS, ...(MAKE_EXCLUSIONS[makeKey(vehicle?.brand)] || [])];
  return terms.some(term => cText.includes(term) && !vText.includes(term));
};
// Strict comparable filter — only true matches on variant + engine displacement.
const specNorm = (s) => (s == null ? '' : String(s))
  .toLowerCase().replace(/\(.*?\)/g, ' ').replace(/[^a-z0-9. ]/g, ' ').replace(/\s+/g, ' ').trim();
const displacement = (s) => {
  const t = String(s || '');
  const m = t.match(/(\d(?:\.\d)?)\s*l/i) || t.match(/(\d\.\d)/);
  return m ? m[1] : null;
};
// Source listings are inconsistent about WHERE they put things: the variant may live in
// the model text ("Virage - 5.3 Widebody" with variant "Base Model"), engine size may be
// absent or buried in the model, and "Base Model"/"Standard" are placeholders, not variants.
// So identity is matched against the COMBINED text, and a comp is only excluded when it
// states something that genuinely CONFLICTS — never merely for missing data.
const PLACEHOLDER_VARIANTS = new Set(['base model', 'standard', 'base', 'n/a', 'other']);
const identityText = (c) => specNorm(`${c.model || ''} ${c.variant || ''}`);
const knownVariant = (c) => {
  const cv = specNorm(c.variant);
  return cv && !PLACEHOLDER_VARIANTS.has(cv) ? cv : null;
};
const compDisplacement = (c) => displacement(c.engine_size) || displacement(c.model);


// Drop unusually high sales (race/provenance cars) per currency using a Tukey fence.
// Drop unusually high sales (race/provenance) via a Tukey fence on EUR-normalized prices.
const filterPriceOutliers = (comps) => {
  const FENCE = 3.0; // lower = stricter (2.0), higher = only extreme (4.0)
  const prices = comps.map(c => c._priceEUR).filter(p => p > 0).sort((a, b) => a - b);
  if (prices.length < 8) return comps; // too few to judge
  const q = (p) => { const i = (prices.length - 1) * p, lo = Math.floor(i), hi = Math.ceil(i); return prices[lo] + (prices[hi] - prices[lo]) * (i - lo); };
  const q1 = q(0.25), q3 = q(0.75), upper = q3 + FENCE * (q3 - q1);
  return comps.filter(c => !(c._priceEUR > 0 && c._priceEUR > upper));
};
const CONDITION_MULT = { Concours: 1.30, Excellent: 1.15, Good: 1.00, Fair: 0.82, Poor: 0.60 };
const pctOf = (arr, p) => { const i = (arr.length - 1) * p, lo = Math.floor(i), hi = Math.ceil(i); return arr[lo] + (arr[hi] - arr[lo]) * (i - lo); };
const compKmOf = (c) => {
  if (!c.mileage) return null;
  const n = Number(c.mileage);
  if (!(n > 0)) return null;
  return String(c.mileage_unit).toLowerCase() === 'miles' ? n * 1.60934 : n;
};

// Market value = median of closest comps, adjusted by explicit, explainable multipliers.
// Below this, a "median" is one or two sales rather than a market — no range, no
// distribution, no trend. Better to show nothing and let the owner enter their own
// figure than to publish a number that looks computed but isn't.
const MIN_COMPS_FOR_ESTIMATE = 3;

export const calcEstimatedValue = (allComparables, collectorScore, v) => {
  const relevant = allComparables.filter(c => (c._matchScore || 0) >= 35);
  const comparables = relevant.length >= 3 ? relevant : allComparables;
  const allPrices = comparables.map(c => c._priceEUR).filter(p => p > 0);
  if (allPrices.length < MIN_COMPS_FOR_ESTIMATE) return null;

  const soldComps  = comparables.filter(c => c.sold_price && Number(c.sold_price) > 0 && c._priceEUR > 0);
  const soldPrices = soldComps.map(c => c._priceEUR).filter(p => p > 0);
  const usingSoldOnly = soldPrices.length >= 2;
  const basis = (usingSoldOnly ? soldPrices : allPrices).slice().sort((a, b) => a - b);

  const median = pctOf(basis, 0.5);
  const p10 = pctOf(basis, 0.10), p25 = pctOf(basis, 0.25);
  const p75 = pctOf(basis, 0.75), p90 = pctOf(basis, 0.90);

  // ── Condition (biggest single driver) ──
  const condF = CONDITION_MULT[v?.condition_grade] ?? 1.00;

  // ── Mileage vs the typical comp ──
  let mileF = 1.00, medianCompKm = null;
  const compKm = comparables.map(compKmOf).filter(x => x > 0).sort((a, b) => a - b);
  const vKm = Number(v?.mileage_score) || 0;
  if (vKm > 0 && compKm.length >= 3) {
    medianCompKm = pctOf(compKm, 0.5);
    if (medianCompKm > 0) {
      mileF = 1 - ((vKm / medianCompKm) - 1) * 0.15;
      mileF = Math.max(0.88, Math.min(1.10, mileF));
    }
  }

  // ── Originality ──
  const origItems = [v?.original_paint, v?.matching_numbers, v?.original_interior, v?.oem_parts, v?.factory_wheels, v?.original_radio, v?.no_modifications];
  let origF = 1.00;
  if (origItems.some(Boolean)) {
    origF = 0.95 + (origItems.filter(Boolean).length / 7) * 0.15;
    if (v?.matching_numbers) origF += 0.02; // matching numbers carries outsized weight
    origF = Math.min(origF, 1.10);
  }

  // ── Service history ──
  const svcF = v?.service_history_status === 'Complete' ? 1.05
    : v?.service_history_status === 'None' ? 0.97 : 1.00;

  // ── Rarity & provenance ── driven directly by the Supply multiplier (remaining
  // units / total production) and the Special Edition multiplier, so a car with
  // 50 or fewer left, or a genuine special edition, moves the estimated value by
  // a real, visible amount — not a token 1–2%. Clamped wider than before (up to
  // 1.80x) specifically so these two, combined, can actually reach the >=1.5x
  // impact the owner asked for.
  const supplyM = calcSupplyMultiplier(v).multiplier;
  const specialM = calcSpecialEditionMultiplier(v);
  let rarF = supplyM * specialM;
  if (v?.factory_color_rarity === 'Extremely Rare') rarF *= 1.02;
  else if (v?.factory_color_rarity === 'Rare') rarF *= 1.01;
  if (v?.colour_desirability === 'Highly Desirable') rarF *= 1.02;
  else if (v?.colour_desirability === 'Less Desirable') rarF *= 0.97;
  if (v?.single_owner) rarF *= 1.01;
  if (v?.museum_history) rarF *= 1.01;
  if (v?.celebrity_owned) rarF *= 1.02;
  if (v?.matching_vin_docs) rarF *= 1.01;
  rarF = Math.max(0.90, Math.min(rarF, 1.80));

  // ── Restoration quality ── a documented, professional or frame-off restoration
  // is worth more than an undocumented one; an unfinished project is worth less.
  let restF = 1.00;
  if (v?.restoration_status === 'Restored' && v?.frame_off_restoration) restF = 1.04;
  else if (v?.restoration_status === 'Restored' && v?.professional_restoration) restF = 1.02;
  else if (v?.restoration_status === 'Project') restF = 0.85;
  else if (v?.restoration_status === 'Partially Restored') restF = 0.95;
  if (v?.major_restoration_docs) restF *= 1.01;

  // ── Transmission ── a manual gearbox commands a real premium on most classics.
  const transF = v?.transmission_type === 'Manual' ? 1.02 : 1.00;

  // ── Market demand ──
  const demF = v?.market_trend === 'Appreciating' ? 1.05
    : v?.market_trend === 'Depreciating' ? 0.95 : 1.00;

  // ── Modifications ──
  const modF = v?.modification_impact === 'Value Positive' ? 1.05
    : v?.modification_impact === 'Value Negative' ? 0.92
    : v?.modification_impact === 'Heavily Modified' ? 0.80 : 1.00;

  // ── Category / segment multiplier ── a manual override (e.g. "air-cooled
  // Porsche market premium 1.2x") the owner can set directly in the Rarity tab.
  const catF = Number(v?.category_multiplier) > 0 ? Number(v.category_multiplier) : 1.00;

  const totalMultiplier = condF * mileF * origF * svcF * rarF * restF * transF * demF * modF * catF;

  // Anchor on the median, then clamp so it can't run away from the observed market
  const raw = median * totalMultiplier;
  const floor = p10 * 0.80, ceil = p90 * 1.25;
  const estimatedValue = Math.max(floor, Math.min(ceil, raw));
  const clamped = raw < floor || raw > ceil;

  const minPrice = basis[0];
  const maxPrice = basis[basis.length - 1];
  const avgPrice = basis.reduce((s, p) => s + p, 0) / basis.length;
  const displayMin = Math.min(...allPrices);
  const displayMax = Math.max(...allPrices);
  const displayAvg = allPrices.reduce((s, p) => s + p, 0) / allPrices.length;

  let confidence = Math.min(allPrices.length * 8, 40);
  if (soldComps.length >= 3) confidence += 20; else if (soldComps.length > 0) confidence += 10;
  if (comparables.some(c => c.sale_date)) confidence += 10;
  if (comparables.length >= 10) confidence += 10;
  confidence += Math.round((collectorScore?.completeness || 0) * 0.2); // full data = +20
  confidence = Math.min(confidence, 100);

  return {
    median, p10, p25, p75, p90,
    minPrice, maxPrice, avgPrice,
    displayMin, displayMax, displayAvg,
    estimatedValue, totalMultiplier, clamped,
    medianCompKm, vehicleKm: vKm || null,
    factors: {
      condF: Number(condF.toFixed(3)), mileF: Number(mileF.toFixed(3)),
      origF: Number(origF.toFixed(3)), svcF: Number(svcF.toFixed(3)),
      rarF: Number(rarF.toFixed(3)),  demF: Number(demF.toFixed(3)),
      modF: Number(modF.toFixed(3)),
      restF: Number(restF.toFixed(3)), transF: Number(transF.toFixed(3)),
      catF: Number(catF.toFixed(3)),
      supplyMultiplier: Number(supplyM.toFixed(3)), specialEditionMultiplier: Number(specialM.toFixed(3)),
    },
    confidence,
    soldCount: soldComps.length,
    askingCount: allPrices.length - soldComps.length,
    usingSoldOnly,
    basisCount: basis.length,
  };
};// ── Investment Score ──────────────────────────────────────────────────────────
const calcInvestmentScore = (val, collectorScore, yearlyRate) => {
  if (!val) return null;
  let score = 0;

  // Yearly appreciation rate (max 35 pts)
  if (yearlyRate !== null) {
    if (yearlyRate >= 20)      score += 35;
    else if (yearlyRate >= 10) score += 28;
    else if (yearlyRate >= 5)  score += 20;
    else if (yearlyRate >= 0)  score += 12;
    else                       score += 0;
  }

  // Rarity contribution from collector score (max 20 pts)
  const rarityScore = collectorScore?.breakdown?.Rarity?.score || 0;
  score += Math.round((rarityScore / 20) * 20);

  // Provenance contribution (max 10 pts)
  const provScore = collectorScore?.breakdown?.Provenance?.score || 0;
  score += Math.round((provScore / 10) * 10);

  // Market trend (max 20 pts)
  const trendMap = { Appreciating: 20, Stable: 10, Depreciating: 0 };
  score += trendMap[val.market_trend] || 10;

  // Category multiplier bonus (max 15 pts)
  const catMult = Number(val.category_multiplier || 1);
  if (catMult >= 1.5)      score += 15;
  else if (catMult >= 1.2) score += 10;
  else if (catMult >= 1.0) score += 5;

  return Math.min(score, 100);
};

// ── Liquidity Score ───────────────────────────────────────────────────────────
const calcLiquidityScore = (comparables, demandFactor) => {
  if (!comparables || comparables.length === 0) return null;
  let score = 0;

  // Number of comparables (max 30 pts)
  const compCount = comparables.length;
  if (compCount >= 10)     score += 30;
  else if (compCount >= 6) score += 22;
  else if (compCount >= 3) score += 14;
  else                     score += 6;

  // Sold prices available (max 25 pts) — more sold = more liquid
  const soldCount = comparables.filter(c => c.sold_price && Number(c.sold_price) > 0).length;
  if (soldCount >= 5)      score += 25;
  else if (soldCount >= 3) score += 18;
  else if (soldCount >= 1) score += 10;

  // Auction bids (max 20 pts)
  const bidsData = comparables.filter(c => c.num_bids && Number(c.num_bids) > 0);
  if (bidsData.length > 0) {
    const avgBids = bidsData.reduce((s, c) => s + Number(c.num_bids), 0) / bidsData.length;
    if (avgBids >= 30)      score += 20;
    else if (avgBids >= 15) score += 14;
    else if (avgBids >= 5)  score += 8;
    else                    score += 4;
  } else {
    score += 8; // neutral if no bid data
  }

  // Market demand factor (max 25 pts)
  const demandMap = { 'Strong Demand': 25, 'Increasing': 18, 'Stable': 12, 'Falling': 4 };
  score += demandMap[demandFactor] || 12;

  return Math.min(score, 100);
};

// ── Auction Confidence Score ──────────────────────────────────────────────────
const calcAuctionConfidence = (comparables) => {
  if (!comparables || comparables.length === 0) return null;
  let score = 0;

  // Total data points (max 25 pts)
  const total = comparables.length;
  if (total >= 10)     score += 25;
  else if (total >= 6) score += 18;
  else if (total >= 3) score += 12;
  else                 score += 5;

  // % sold vs asking (max 30 pts) — more sold = higher confidence
  const soldCount   = comparables.filter(c => c.sold_price && Number(c.sold_price) > 0).length;
  const soldRatio   = soldCount / total;
  if (soldRatio >= 0.7)      score += 30;
  else if (soldRatio >= 0.4) score += 20;
  else if (soldRatio >= 0.1) score += 10;

  // Reserve met ratio (max 20 pts)
  const auctionComps   = comparables.filter(c => c.reserve_met !== null && c.reserve_met !== undefined);
  if (auctionComps.length > 0) {
    const metCount = auctionComps.filter(c => c.reserve_met).length;
    const metRatio = metCount / auctionComps.length;
    if (metRatio >= 0.8)      score += 20;
    else if (metRatio >= 0.5) score += 13;
    else                      score += 5;
  } else {
    score += 10; // neutral if no reserve data
  }

  // Recency of comparables (max 25 pts)
  const datedComps = comparables.filter(c => c.sale_date);
  if (datedComps.length > 0) {
    const mostRecent = new Date(Math.max(...datedComps.map(c => new Date(c.sale_date))));
    const monthsOld  = (new Date() - mostRecent) / (1000 * 60 * 60 * 24 * 30);
    if (monthsOld <= 3)       score += 25;
    else if (monthsOld <= 6)  score += 18;
    else if (monthsOld <= 12) score += 10;
    else                      score += 4;
  } else {
    score += 8; // neutral if no date data
  }

  return Math.min(score, 100);
};

// ── Helper Components ─────────────────────────────────────────────────────────
// Phone: the row of section tabs, with a thin line underneath that shows how far along the
// row you are, so it is obvious there are more tabs to scroll to.
const ScrollTabs = ({ children }) => {
  const ref = useRef(null);
  const [m, setM] = useState({ left: 0, width: 100, fits: true });
  const update = useCallback(() => {
    const el = ref.current;
    if (!el) return;
    const { scrollLeft, scrollWidth, clientWidth } = el;
    const next = scrollWidth <= clientWidth + 1
      ? { left: 0, width: 100, fits: true }
      : { left: Math.round((scrollLeft / scrollWidth) * 1000) / 10, width: Math.round((clientWidth / scrollWidth) * 1000) / 10, fits: false };
    setM(prev => (prev.left === next.left && prev.width === next.width && prev.fits === next.fits ? prev : next));
  }, []);
  useEffect(() => {
    update();
    window.addEventListener('resize', update);
    return () => window.removeEventListener('resize', update);
  }, [update]);
  useEffect(() => { update(); });   // tab labels change (e.g. the comparables count)
  return (
    <>
      <div className="ci-tabs" ref={ref} onScroll={update}>{children}</div>
      {!m.fits && (
        <div className="ci-track" aria-hidden="true">
          <div className="ci-thumb" style={{ left: `${m.left}%`, width: `${m.width}%` }} />
        </div>
      )}
    </>
  );
};

const ScoreRing = ({ score }) => {
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#4f8ef7' : score >= 40 ? '#eab308' : '#ef4444';
  const label = score >= 80 ? 'Exceptional' : score >= 60 ? 'Good' : score >= 40 ? 'Average' : 'Poor';
  const circumference = 2 * Math.PI * 40;
  const offset = circumference - (score / 100) * circumference;
  return (
    <div style={{ textAlign: 'center', position: 'relative', display: 'inline-block' }}>
      <svg width={100} height={100} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={50} cy={50} r={40} fill="none" stroke="var(--surface2)" strokeWidth={8} />
        <circle cx={50} cy={50} r={40} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={circumference} strokeDashoffset={offset}
          strokeLinecap="round" style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      </svg>
      <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', textAlign: 'center' }}>
        <div style={{ fontSize: 22, fontWeight: 800, color, lineHeight: 1 }}>{score}</div>
        <div style={{ fontSize: 9, color: 'var(--text3)', marginTop: 2 }}>/ 100</div>
      </div>
      <div style={{ fontSize: 11, fontWeight: 600, color, marginTop: 4 }}>{label}</div>
    </div>
  );
};

const ConfidenceBar = ({ score }) => {
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#4f8ef7' : score >= 40 ? '#eab308' : '#ef4444';
  const label = score >= 80 ? 'High' : score >= 60 ? 'Good' : score >= 40 ? 'Moderate' : 'Low';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
        <span style={{ fontSize: 14, color: 'var(--text2)' }}>Confidence Score</span>
        <span style={{ fontSize: 13, fontWeight: 700, color }}>{score}% — {label}</span>
      </div>
      <div style={{ height: 8, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
        <div style={{ height: '100%', width: `${score}%`, background: `linear-gradient(90deg, ${color}, ${color}99)`, borderRadius: 4, transition: 'width 0.6s ease' }} />
      </div>
      <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 4 }}>Add more sold comparables to increase confidence</div>
    </div>
  );
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '10px 14px', boxShadow: '0 8px 32px rgba(0,0,0,0.3)' }}>
        <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 4 }}>{label}</div>
        <div style={{ fontSize: 14, fontWeight: 700, color: '#4f8ef7' }}>{formatCurrency(payload[0].value)}</div>
      </div>
    );
  }
  return null;
};

const FormSection = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div style={{ border: '1px solid var(--border)', borderRadius: 8, overflow: 'hidden', marginBottom: 12 }}>
      <div onClick={() => setOpen(!open)} tabIndex={-1} style={{ padding: '11px 14px', background: 'var(--surface2)', cursor: 'pointer', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontWeight: 600, fontSize: 13 }}>
        {title} {open ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
      </div>
      {open && <div style={{ padding: '14px', display: 'flex', flexDirection: 'column', gap: 12 }}>{children}</div>}
    </div>
  );
};

// ── Description Tab ───────────────────────────────────────────────────────────
// Free-text "about this car" story, separate from the Edit Data modal's admin
// Notes field — stored directly on the vehicle record (vehicles.description).
//
// The field itself is still plain text — no schema change — but it now
// understands a small, forgiving set of markdown so the saved story can read
// like a proper page instead of a wall of text: "# " / "## " / "### " for
// headers, "- " / "* " for bullets, "**bold**" and "*italic*". escapeHtml()
// runs first, so the only tags that can ever reach the page are the ones this
// renderer adds itself — never anything typed into the textarea.
const escapeHtml = (s) => s
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
  .replace(/"/g, '&quot;').replace(/'/g, '&#39;');

const formatInline = (s) => s
  .replace(/\*\*(.+?)\*\*/g, '<strong style="color:var(--text);font-weight:700;">$1</strong>')
  .replace(/(^|[^*])\*([^*\s][^*]*?)\*(?!\*)/g, '$1<em>$2</em>');

const HEADING_STYLE = {
  h3: 'font-size:19px;font-weight:700;color:var(--text);margin:22px 0 8px;padding-bottom:7px;border-bottom:1px solid var(--border);',
  h4: 'font-size:15.5px;font-weight:700;color:var(--accent);margin:18px 0 6px;',
  h5: 'font-size:13px;font-weight:700;color:var(--text2);text-transform:uppercase;letter-spacing:.5px;margin:14px 0 4px;',
};

const renderDescription = (raw) => {
  if (!raw || !raw.trim()) return '';
  const lines = raw.split(/\r?\n/);
  let html = '';
  let inList = false;
  let para = [];
  let first = true;
  const closeList = () => { if (inList) { html += '</ul>'; inList = false; } };
  const flushPara = () => {
    if (para.length) {
      html += `<p style="font-size:14px;line-height:1.75;color:var(--text2);margin:0 0 12px;">${para.join(' ')}</p>`;
      para = [];
    }
  };
  for (const raw_ of lines) {
    const line = escapeHtml(raw_).trim();
    if (line === '') { flushPara(); closeList(); continue; }
    const h1 = line.match(/^#\s+(.*)/), h2 = line.match(/^##\s+(.*)/), h3 = line.match(/^###\s+(.*)/);
    const li = line.match(/^[-*]\s+(.*)/);
    const heading = h3 || h2 || h1;
    if (heading) {
      flushPara(); closeList();
      const tag = h3 ? 'h5' : h2 ? 'h4' : 'h3';
      const style = HEADING_STYLE[tag] + (first ? 'margin-top:0;' : '');
      html += `<${tag} style="${style}">${formatInline(heading[1])}</${tag}>`;
      first = false;
      continue;
    }
    if (li) {
      flushPara();
      if (!inList) { html += '<ul style="margin:0 0 12px;padding-left:20px;">'; inList = true; }
      html += `<li style="font-size:14px;line-height:1.7;color:var(--text2);margin-bottom:4px;">${formatInline(li[1])}</li>`;
      first = false;
      continue;
    }
    closeList();
    para.push(formatInline(line));
    first = false;
  }
  flushPara(); closeList();
  return html;
};

const DESC_TOOLBAR = [
  { label: 'H1', title: 'Heading', wrap: 'line', prefix: '# ' },
  { label: 'H2', title: 'Subheading', wrap: 'line', prefix: '## ' },
  { label: 'B', title: 'Bold', wrap: 'inline', mark: '**', style: { fontWeight: 700 } },
  { label: 'I', title: 'Italic', wrap: 'inline', mark: '*', style: { fontStyle: 'italic' } },
  { label: '•', title: 'Bullet list', wrap: 'line', prefix: '- ' },
];

const VehicleDescriptionTab = ({ vehicle, onSaved }) => {
  const [text, setText] = useState(vehicle?.description || '');
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [mode, setMode] = useState(vehicle?.description ? 'preview' : 'edit');
  const taRef = useRef(null);

  useEffect(() => {
    setText(vehicle?.description || '');
    setDirty(false);
    setMode(vehicle?.description ? 'preview' : 'edit');
  }, [vehicle?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async () => {
    if (!vehicle?.id) return;
    setSaving(true);
    const { error } = await supabase.from('vehicles').update({ description: text || null }).eq('id', vehicle.id);
    setSaving(false);
    if (error) { toast.error('Error: ' + error.message); return; }
    toast.success('Description saved!');
    setDirty(false);
    onSaved?.(text || null);
    setMode('preview');
  };

  const applyTool = (tool) => {
    const ta = taRef.current;
    if (!ta) return;
    const { selectionStart: s, selectionEnd: e } = ta;
    let next, cursor;
    if (tool.wrap === 'inline') {
      next = text.slice(0, s) + tool.mark + text.slice(s, e) + tool.mark + text.slice(e);
      cursor = e === s ? s + tool.mark.length : e + tool.mark.length * 2;
    } else {
      const lineStart = text.lastIndexOf('\n', s - 1) + 1;
      next = text.slice(0, lineStart) + tool.prefix + text.slice(lineStart);
      cursor = s + tool.prefix.length;
    }
    setText(next);
    setDirty(true);
    requestAnimationFrame(() => { ta.focus(); ta.setSelectionRange(cursor, cursor); });
  };

  const html = useMemo(() => renderDescription(text), [text]);

  return (
    <div className="card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12, marginBottom: 6, flexWrap: 'wrap' }}>
        <div>
          <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 6 }}>About this car</div>
          <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6, maxWidth: 560 }}>
            The story of this car — how you found it, what makes it special, ownership history you
            want to remember. Separate from the admin Notes in Edit Data, and doesn't affect the
            Collector Score.
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6, background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: 3, flexShrink: 0 }}>
          <button
            className="btn btn-sm"
            style={{ background: mode === 'edit' ? 'var(--accent)' : 'transparent', color: mode === 'edit' ? '#fff' : 'var(--text2)', border: 'none' }}
            onClick={() => setMode('edit')}>
            Edit
          </button>
          <button
            className="btn btn-sm"
            style={{ background: mode === 'preview' ? 'var(--accent)' : 'transparent', color: mode === 'preview' ? '#fff' : 'var(--text2)', border: 'none' }}
            onClick={() => setMode('preview')}>
            Preview
          </button>
        </div>
      </div>

      {mode === 'edit' ? (
        <>
          <div style={{ display: 'flex', gap: 4, marginBottom: 8, flexWrap: 'wrap' }}>
            {DESC_TOOLBAR.map(t => (
              <button
                key={t.label}
                type="button"
                title={t.title}
                className="btn btn-ghost btn-sm"
                style={{ minWidth: 30, padding: '4px 9px', ...(t.style || {}) }}
                onMouseDown={e => e.preventDefault()}
                onClick={() => applyTool(t)}>
                {t.label}
              </button>
            ))}
          </div>
          <textarea
            ref={taRef}
            className="form-input"
            style={{ minHeight: 260, lineHeight: 1.6, resize: 'vertical', fontFamily: 'var(--font)' }}
            value={text}
            onChange={e => { setText(e.target.value); setDirty(true); }}
            placeholder="e.g. Bought from the original owner in 2019, matching-numbers example, first driven by my grandfather on its delivery day…&#10;&#10;# A short chapter title&#10;Then just write. Use ## for a smaller heading, - for a list, **bold** for emphasis."
          />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
            Formatting: <code>#</code> heading · <code>##</code> subheading · <code>-</code> bullet · <code>**bold**</code> · <code>*italic*</code>
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 12 }}>
            <button className="btn btn-primary" disabled={saving || !dirty} onClick={save}>
              {saving ? 'Saving…' : 'Save Description'}
            </button>
          </div>
        </>
      ) : (
        html ? (
          <div style={{ paddingTop: 4 }} dangerouslySetInnerHTML={{ __html: html }} />
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: 'var(--text3)' }}>
            <div style={{ fontSize: 14, marginBottom: 12 }}>No story written yet.</div>
            <button className="btn btn-primary btn-sm" onClick={() => setMode('edit')}>Write about this car</button>
          </div>
        )
      )}
    </div>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────
// Finds the sold / asking comparables for a vehicle (the same lookup the Intelligence screen
// uses, also used by the phone vehicle page for its price-trend graph).
export const loadComparables = async (vehicle) => {
  const modelBase = vehicle.model.split(' ')[0];

  // These MUST mirror fp_make_group / fp_model_base in SQL, or the indexed
  // equality lookup won't match what was written to the table.
  const makeGroupOf = (m) => {
    const t = (m || '').trim().toLowerCase();
    if (t.startsWith('porsche')) return 'porsche';
    if (t.startsWith('mercedes')) return 'mercedes-benz';
    if (t.startsWith('land rover') || t.startsWith('range rover') || t === 'rover' || t.startsWith('rover ')) return 'land rover';
    if (t === 'vw' || t.startsWith('volkswagen')) return 'volkswagen';
    if (t.startsWith('rolls')) return 'rolls-royce';
    if (t.startsWith('alfa')) return 'alfa romeo';
    if (t.startsWith('aston')) return 'aston martin';
    return t;
  };
  const modelBaseOf = (v) => (v || '').trim().toLowerCase().split(/[\s,/]+/)[0] || '';

  const COMP_COLS = 'id, make, model, variant, year, mileage, mileage_unit, engine_size, transmission, drive_side, exterior_colour, interior_colour, fuel_type, sold_price, asking_price, currency, sale_date, source_website, source_type, listing_url, photo_url, condition, created_by, normalized_generation, normalized_body_style, taxonomy_match_id';
  const yFrom = (vehicle.year || 1900) - 5;
  const yTo   = (vehicle.year || 2100) + 5;

  // ── Tiered matching ──────────────────────────────────────────────────────
  // Each tier is more precise than the one below it. We fall through only when a
  // tier returns too few rows to value from, so a precisely-specified vehicle
  // gets precise comparables and a vaguely-specified one still gets something.
  const base = () => supabase.from('comparables_shared').select(COMP_COLS)
    .gte('year', yFrom).lte('year', yTo);

  let data = null, compError = null, matchTier = null;

  // TIER 1 — the structured taxonomy key (e.g. PORS_911_997_TARGA). Set when the
  // owner picked their car from the catalogue. Exact, indexed, no text comparison.
  if (vehicle.taxonomy_match_id) {
    const r = await base().eq('taxonomy_match_id', vehicle.taxonomy_match_id).limit(5000);
    compError = r.error;
    if (!r.error && (r.data || []).length >= MIN_COMPS_FOR_ESTIMATE) {
      data = r.data; matchTier = 'taxonomy';
    }
  }

  // TIER 2 — normalised make/model plus whichever of generation, body style and
  // variant the vehicle actually states. Adding each one narrows the set; we stop
  // narrowing if it would take us below the minimum.
  if (!data && !compError && vehicle.model) {
    let q = base()
      .ilike('make', vehicle.brand)
      .ilike('model', vehicle.model);
    if (vehicle.generation)  q = q.eq('normalized_generation', vehicle.generation);
    if (vehicle.body_style)  q = q.eq('normalized_body_style', vehicle.body_style);
    if (vehicle.variant)     q = q.ilike('variant', `%${vehicle.variant}%`);
    const r = await q.limit(5000);
    compError = r.error;
    if (!r.error && (r.data || []).length >= MIN_COMPS_FOR_ESTIMATE) {
      data = r.data; matchTier = 'normalised';
    }

    // Same again without the variant filter — variant wording varies most between
    // sources, so it's the first constraint worth relaxing.
    if (!data && !compError && vehicle.variant) {
      let q2 = base().ilike('make', vehicle.brand).ilike('model', vehicle.model);
      if (vehicle.generation) q2 = q2.eq('normalized_generation', vehicle.generation);
      if (vehicle.body_style) q2 = q2.eq('normalized_body_style', vehicle.body_style);
      const r2 = await q2.limit(5000);
      compError = r2.error;
      if (!r2.error && (r2.data || []).length >= MIN_COMPS_FOR_ESTIMATE) {
        data = r2.data; matchTier = 'normalised-loose';
      }
    }
  }

  // TIER 3 — the grouped keys. Covers out-of-scope makes (63% of listings) and
  // anything entered as free text rather than chosen from the catalogue.
  if (!data && !compError) {
    const r = await base()
      .eq('make_group', makeGroupOf(vehicle.brand))
      .eq('model_base', modelBaseOf(modelBase))
      .limit(5000);
    compError = r.error;
    if (!r.error && (r.data || []).length > 0) { data = r.data; matchTier = 'grouped'; }
  }

  // TIER 4 — pattern match on the model text. Slowest, so genuinely last.
  if (!data && !compError) {
    const { data: aliases } = await supabase
      .from('model_aliases').select('alias')
      .ilike('make', vehicle.brand)
      .ilike('canonical_model', modelBase);
    const aliasPatterns = aliases?.length
      ? aliases.map(a => `model.ilike.%${a.alias}%`).join(',')
      : `model.ilike.%${modelBase}%`;
    const r = await base().ilike('make', vehicle.brand).or(aliasPatterns).limit(5000);
    compError = r.error;
    if (!r.error) { data = r.data; matchTier = 'text'; }
  }

  if (compError) return { error: compError, tier: matchTier, scored: [], hidden: 0 };
  
  // Deduplicate by source_website + asking_price + sold_price + mileage
  const seen = new Set();
  const deduped = (data || []).filter(c => {
    const key = `${c.source_website}-${c.asking_price}-${c.sold_price}-${c.mileage}-${c.year}-${c.variant}-${c.country}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
  // Normalize every comp's price to EUR (approx current rates)
  const withEUR = deduped.map(c => ({ ...c, _priceEUR: toEUR(c.sold_price || c.asking_price, c.currency) }));
  // Exclude race/provenance cars by keyword (model + variant)
  const noProv = withEUR.filter(c => !isProvenanceCar(c, vehicle));
  // Strict filter: variant + engine + body (911 SC ≠ 930 Turbo; coupe ≠ cabriolet)
  const strict = noProv.filter(c => isStrictMatch(vehicle, c));
  // Remove unusually high sales (race/provenance), on EUR-normalized prices
  const kept = filterPriceOutliers(strict);
  // Score each comparable by how well it matches the selected vehicle, best first
  const scored = kept.map(c => {
    const { score, pct, fields } = scoreComparable(vehicle, c);
    return { ...c, _matchScore: score, _matchPct: pct, _matchFields: fields };
  });
  scored.sort((a, b) => (b._matchPct - a._matchPct) || (b._matchScore - a._matchScore) ||
    (new Date(b.sale_date || 0) - new Date(a.sale_date || 0)));
  return { error: null, tier: matchTier, scored, hidden: strict.length - kept.length };
};

export const ClassicCarIntelligence = () => {
  const { canUseAI, plan, isTrialing, trialDaysLeft } = usePlan();
  const { role } = useOwner();
  const navigate = useNavigate();
  const [vehicles, setVehicles]     = useState([]);
  const [valuations, setValuations] = useState([]);
  const [histories, setHistories]   = useState([]);
  const [expenses, setExpenses]     = useState([]);
  const [insuranceRecords, setInsuranceRecords] = useState([]);
  const [roadTaxRecords, setRoadTaxRecords]     = useState([]);
  const [serviceRecords, setServiceRecords]     = useState([]);
  const [mechanicalRecords, setMechanicalRecords] = useState([]);
  const [comparables, setComparables] = useState([]);
  const [hiddenOutliers, setHiddenOutliers] = useState(0);
  const [reports, setReports]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [selectedVehicleId, setSelectedVehicleId] = useState('');
  const [activeTab, setActiveTab]   = useState('collector');
  const [showValuationModal, setShowValuationModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal]     = useState(false);
  const [editValuation, setEditValuation] = useState(null);
  const [saving, setSaving]               = useState(false);
  const [factors, setFactors] = useState({ condition: 'Good', mileage: 'Average', originality: 'Mostly Original', service: 'Good History', rarity: 'Common', demand: 'Stable' });
  const [purchasePrice, setPurchasePrice]     = useState(null);
  const [showVehicleList, setShowVehicleList] = useState(true);
  const [carSheet, setCarSheet] = useState(false);   // phone: "Change car" bottom sheet
  const [marketReport, setMarketReport]       = useState(null);
  const [reportLoading, setReportLoading]     = useState(false);
  const [reportError, setReportError]         = useState(null);
  const [savedReports, setSavedReports] = useState([]);

  // Phone: open straight on the car you looked at last time.
  useEffect(() => {
    if (!isNative || selectedVehicleId || !vehicles.length) return;
    try {
      const last = localStorage.getItem('ci-last-vehicle');
      if (last && vehicles.some(v => v.id === last)) setSelectedVehicleId(last);
    } catch { /* remembering is optional */ }
  }, [vehicles]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isNative || !selectedVehicleId) return;
    try { localStorage.setItem('ci-last-vehicle', selectedVehicleId); } catch { /* optional */ }
  }, [selectedVehicleId]);

  const selectedVehicle = vehicles.find(v => v.id === selectedVehicleId);
  const selectedVal     = valuations.find(v => v.vehicle_id === selectedVehicleId);
  const selectedHist    = histories.filter(h => h.vehicle_id === selectedVehicleId);

  const fetchData = useCallback(async () => {
    setLoading(true);
    const [v, val, hist, rep, exp, ins, rt, svc, mech] = await Promise.all([
      supabase.from('vehicles').select('*, vehicle_photos(*)').in('status', ['active','inactive','classic','daneira','immobilized','dokimi_test','service','restoration']).order('brand'),
      supabase.from('vehicle_valuations').select('*'),
      supabase.from('valuation_history').select('*').order('valuation_date', { ascending: true }),
      supabase.from('valuation_reports').select('*').order('report_date', { ascending: false }),
      supabase.from('expenses').select('*'),
      supabase.from('insurance_records').select('*'),
      supabase.from('road_tax_records').select('*'),
      supabase.from('service_records').select('*'),
      supabase.from('mechanical_reports').select('*'),
    ]);
    setVehicles(v.data || []);
    setValuations(val.data || []);
    setHistories(hist.data || []);
    setReports(rep.data || []);
    setExpenses(exp.data || []);
    setInsuranceRecords(ins.data || []);
    setRoadTaxRecords(rt.data || []);
    setServiceRecords(svc.data || []);
    setMechanicalRecords(mech.data || []);
    setLoading(false);
  }, []);

  const compReqRef = useRef(0);
  const [compsOk, setCompsOk] = useState(false);   // did the last comparables query succeed?
  const [compsLoading, setCompsLoading] = useState(false);
  const [compTier, setCompTier] = useState(null);

  const fetchComparables = useCallback(async () => {
    if (!selectedVehicleId || !selectedVehicle) { setComparables([]); setCompsLoading(false); return; }
    setCompsLoading(true);
    // Each run claims a ticket. A slow response from a previously-selected vehicle
    // must not overwrite the current one's results.
    const reqId = ++compReqRef.current;
    const reqVehicleId = selectedVehicleId;
    
    const res = await loadComparables(selectedVehicle);
    if (reqId === compReqRef.current) setCompTier(res.tier);
    if (res.error) {
      console.error('Comparables query failed:', res.error);
      toast.error('Could not load comparables: ' + res.error.message);
      if (reqId === compReqRef.current) { setCompsOk(false); setComparables([]); setCompsLoading(false); }
      return;
    }
    if (reqId !== compReqRef.current) return;
    setHiddenOutliers(res.hidden);
    const scored = res.scored;
    // Discard if the user has since selected a different vehicle
    if (reqId !== compReqRef.current || reqVehicleId !== selectedVehicleId) return;
    setCompsOk(true);
    setComparables(scored);
    setCompsLoading(false);
  }, [selectedVehicleId, selectedVehicle]);

  const fetchSavedReports = useCallback(async () => {
    if (!selectedVehicleId) { setSavedReports([]); setMarketReport(null); return; }
    const { data } = await supabase
      .from('market_intelligence_reports')
      .select('*')
      .eq('vehicle_id', selectedVehicleId)
      .order('generated_at', { ascending: false });
    setSavedReports(data || []);

    // Auto-load the most recent report
    if (data && data.length > 0) {
      const r = data[0];
      setMarketReport({
        investment_verdict:        r.investment_verdict,
        verdict_reasoning:         r.verdict_reasoning,
        key_value_drivers:         r.key_value_drivers,
        best_season_to_sell:       r.best_season_to_sell,
        best_season_reasoning:     r.best_season_reasoning,
        recommended_holding_years: r.recommended_holding_years,
        holding_reasoning:         r.holding_reasoning,
        next_peak_year:            r.next_peak_year,
        peak_year_reasoning:       r.peak_year_reasoning,
        appreciation_forecast: {
          year_1:  r.forecast_year_1,
          year_3:  r.forecast_year_3,
          year_5:  r.forecast_year_5,
          year_10: r.forecast_year_10,
        },
        forecast_reasoning:  r.forecast_reasoning,
        price_trend:         r.price_trend,
        market_narrative: {
          overview:           r.narrative_overview,
          demand_drivers:     r.narrative_demand_drivers,
          regional_analysis:  r.narrative_regional,
          investment_outlook: r.narrative_investment_outlook,
          risk_factors:       r.narrative_risk_factors,
        },
        risk_factors: r.risk_factors,
      });
    } else {
      setMarketReport(null);
    }
  }, [selectedVehicleId]);


  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { fetchComparables(); }, [fetchComparables]);
  useEffect(() => { fetchSavedReports(); }, [fetchSavedReports]);

  useEffect(() => {
    if (!selectedVehicleId) { setPurchasePrice(null); return; }
    const val = valuations.find(v => v.vehicle_id === selectedVehicleId);
    setPurchasePrice(val?.purchase_price ? Number(val.purchase_price) : null);
    if (!val) return;
    const origItems = [val.original_paint, val.matching_numbers, val.original_interior, val.oem_parts, val.factory_wheels, val.original_radio, val.no_modifications];
    const origCount = origItems.filter(Boolean).length;
    let originality = origCount >= 6 ? 'Fully Original' : origCount >= 4 ? 'Mostly Original' : origCount >= 2 ? 'Some Modifications' : 'Heavily Modified';
    let rarity = 'Common';
    if (val.total_production) {
      if (val.total_production < 500) rarity = 'Very Rare';
      else if (val.total_production < 5000) rarity = 'Rare';
      else if (val.total_production < 50000) rarity = 'Desirable';
    } else if (val.special_edition) rarity = 'Rare';
    let mileage = 'Average';
    if (val.mileage_score) {
      if (val.mileage_score < 20000) mileage = 'Extremely Low';
      else if (val.mileage_score < 50000) mileage = 'Low';
      else if (val.mileage_score < 120000) mileage = 'Average';
      else mileage = 'Very High';
    }
    setFactors({
      condition:   { 'Concours': 'Concours', 'Excellent': 'Excellent', 'Good': 'Good', 'Fair': 'Fair', 'Poor': 'Poor' }[val.condition_grade] || 'Good',
      mileage,
      originality,
      service:     { 'Complete': 'Full Documented History', 'Partial': 'Good History', 'None': 'No History' }[val.service_history_status] || 'Good History',
      rarity,
      demand:      'Stable', // updated below from AI report
    });

  }, [selectedVehicleId, valuations]);

  // Derive demand factor from latest AI market intelligence report
  useEffect(() => {
    if (!selectedVehicleId) return;
    const latestReport = savedReports
      .filter(r => r.vehicle_id === selectedVehicleId)
      .sort((a, b) => new Date(b.report_date || b.created_at) - new Date(a.report_date || a.created_at))[0];
    if (!latestReport) return;

    const f1 = Number(latestReport.forecast_year_1 || 0);
    let demand = 'Stable';
    if (f1 > 5) demand = 'Strong Demand';
    else if (f1 < -2) demand = 'Falling';

    setFactors(f => ({ ...f, demand }));
  }, [selectedVehicleId, savedReports]);
  
  const collectorScore = selectedVal ? calcCollectorScore(selectedVal) : null;
  const result = calcEstimatedValue(comparables, collectorScore, selectedVal);
  // ONE source of truth for the headline value: the freshly computed estimate when
  // comparables are available, otherwise whatever is stored. Gain and the displayed
  // value both read this, so they can never disagree the way they used to (the card
  // showed `current_value` while gain was derived from `result.estimatedValue`).
  // Precedence: a comparables-backed estimate wins; otherwise the owner's own
  // figure; otherwise whatever was last stored. `valueSource` drives the label so
  // a hand-entered number is never presented as if the market produced it.
  const manualValue = selectedVal?.manual_market_value != null
    ? Number(selectedVal.manual_market_value) : null;

  const marketValue =
    result?.estimatedValue != null ? Number(result.estimatedValue)
    : manualValue != null          ? manualValue
    : (selectedVal?.current_value != null ? Number(selectedVal.current_value) : null);

  const valueSource =
    result?.estimatedValue != null ? 'market'
    : manualValue != null          ? 'owner'
    : marketValue != null          ? 'stored' : 'none';

  const appreciationAmount = marketValue != null && purchasePrice ? marketValue - purchasePrice : null;
  const appreciationPct    = appreciationAmount && purchasePrice ? (appreciationAmount / purchasePrice) * 100 : null;
  const yearsOwned  = selectedVal?.purchase_date ? differenceInMonths(new Date(), parseISO(selectedVal.purchase_date)) / 12 : 0;
  // True gain/loss: purchase price PLUS every restoration and running cost (expenses,
  // insurance, road tax, service, mechanical) against current value — the same
  // net-of-cost method used for the % shown on the vehicle-picker cards above, rather
  // than just purchase price vs. market value.
  const vExpensesForGain = expenses.filter(e => e.vehicle_id === selectedVehicleId);
  const restorationSpendForGain = vExpensesForGain.filter(e => e.category === 'Restoration').reduce((s, e) => s + Number(e.amount || 0), 0);
  const otherCostsForGain =
    vExpensesForGain.filter(e => e.category !== 'Restoration').reduce((s, e) => s + Number(e.amount || 0), 0) +
    insuranceRecords.filter(e => e.vehicle_id === selectedVehicleId).reduce((s, e) => s + Number(e.cost || 0), 0) +
    roadTaxRecords.filter(e => e.vehicle_id === selectedVehicleId).reduce((s, e) => s + Number(e.amount_paid || 0), 0) +
    serviceRecords.filter(e => e.vehicle_id === selectedVehicleId).reduce((s, e) => s + Number(e.cost || 0), 0) +
    mechanicalRecords.filter(e => e.vehicle_id === selectedVehicleId).reduce((s, e) => s + Number(e.cost || 0), 0);
  const totalInvestedForGain = selectedVal?.purchase_price
    ? Number(selectedVal.purchase_price) + restorationSpendForGain + otherCostsForGain
    : null;
  const gainLoss    = totalInvestedForGain != null && marketValue != null ? marketValue - totalInvestedForGain : null;
  const gainLossPct = gainLoss !== null && totalInvestedForGain ? (gainLoss / totalInvestedForGain) * 100 : null;
  // Annualizing over a very short hold period is unstable — a modest gain in the
  // first couple of months, stretched out to "per year", produces an inflated,
  // noisy number that swings wildly as time passes with no real change in value.
  // So the rate only shows once there's at least 6 months of ownership behind it.
  const MIN_YEARS_FOR_YEARLY_RATE = 0.5;
  const yearlyRate  = gainLossPct !== null && yearsOwned >= MIN_YEARS_FOR_YEARLY_RATE ? gainLossPct / yearsOwned : null;
  const investmentScore   = calcInvestmentScore(selectedVal, collectorScore, yearlyRate);
  const liquidityScore    = calcLiquidityScore(comparables, factors.demand);
  const auctionConfidence = calcAuctionConfidence(comparables);
  // Calculate yearly running costs from actual expense records for this vehicle
  const yearlyCosts = useMemo(() => {
      if (!selectedVehicleId) return 0;
      const currentYear = new Date().getFullYear();
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);

      const sumLastYear = (records, dateField, amountField) => {
        return records
          .filter(r => r.vehicle_id === selectedVehicleId && r[dateField] && new Date(r[dateField]) >= oneYearAgo)
          .reduce((s, r) => s + Number(r[amountField] || 0), 0);
      };

      const expTotal  = sumLastYear(expenses, 'date', 'amount');
      const insTotal  = sumLastYear(insuranceRecords, 'start_date', 'cost') || sumLastYear(insuranceRecords, 'created_at', 'cost');
      const rtTotal   = sumLastYear(roadTaxRecords, 'payment_date', 'amount_paid');
      const svcTotal  = sumLastYear(serviceRecords, 'service_date', 'cost');
      const mechTotal = sumLastYear(mechanicalRecords, 'report_date', 'cost');

      return expTotal + insTotal + rtTotal + svcTotal + mechTotal;
    }, [selectedVehicleId, expenses, insuranceRecords, roadTaxRecords, serviceRecords, mechanicalRecords]);
  const netGain     = gainLoss !== null ? gainLoss - (yearlyCosts * Math.max(yearsOwned, 1)) : null;

  // Auto-save estimated value to current_value so Portfolio/Dashboard stay in sync.
  // Also records how many comparables backed it, and clears a previously auto-derived
  // value once its comparables disappear — an estimate with no market evidence behind
  // it should not keep propagating into Portfolio and fleet totals.
  useEffect(() => {
    if (!selectedVal?.id) return;
    if (!compsOk) return;            // the query failed — do NOT treat that as "no comparables"

    const compCount = comparables.length;
    const rounded = result?.estimatedValue ? Math.round(result.estimatedValue) : null;
    const manualRounded = selectedVal.manual_market_value != null
      ? Math.round(Number(selectedVal.manual_market_value)) : null;
    const patch = {};
    let loggedValue = null;
    let loggedSource = null;

    if (rounded !== null) {
      // A comparables-backed estimate wins.
      if (Number(selectedVal.current_value) !== rounded) {
        patch.current_value = rounded;
        loggedValue = rounded;
        loggedSource = 'Comparables (auto)';
      }
    } else if (manualRounded !== null) {
      // No comparables, but the owner typed their own estimate — that figure IS the
      // value shown on this page (see `marketValue` above), so current_value has to
      // match it or Portfolio/Dashboard silently disagree with what's on screen here.
      if (Number(selectedVal.current_value) !== manualRounded) {
        patch.current_value = manualRounded;
        loggedValue = manualRounded;
        loggedSource = 'Owner estimate (auto)';
      }
    } else if (
      compCount === 0 &&
      selectedVal.valuation_comparables !== 0 &&  // 0 = already cleared; NULL = legacy auto value
      selectedVal.current_value != null
    ) {
      // No comparables and no owner figure either — the stored value has lost its
      // basis and is cleared.
      patch.current_value = null;
    }

    if (Number(selectedVal.valuation_comparables ?? -1) !== compCount) {
      patch.valuation_comparables = compCount;
    }
    if (Object.keys(patch).length === 0) return;

    supabase.from('vehicle_valuations')
      .update(patch)
      .eq('id', selectedVal.id)
      .then(({ error }) => {
        if (error) return;
        setValuations(prev => prev.map(v => v.id === selectedVal.id ? { ...v, ...patch } : v));
        if (patch.current_value === null) {
          toast('Estimated value cleared — no comparables match this vehicle', { icon: 'ℹ️' });
        }
        // Auto-log every real value change into valuation_history — this is what feeds
        // Portfolio's Value History chart, so it fills in on its own over time instead
        // of staying empty until someone logs entries there by hand. One entry per
        // vehicle per day: a same-day re-run (e.g. new comparables found later that
        // day) updates today's entry rather than piling up duplicates.
        if (loggedValue === null) return;
        const today = format(new Date(), 'yyyy-MM-dd');
        supabase.from('valuation_history')
          .select('id')
          .eq('vehicle_id', selectedVal.vehicle_id)
          .eq('valuation_date', today)
          .maybeSingle()
          .then(({ data: existingEntry }) => {
            const write = existingEntry
              ? supabase.from('valuation_history').update({ value: loggedValue, source: loggedSource }).eq('id', existingEntry.id).select()
              : supabase.from('valuation_history').insert({ vehicle_id: selectedVal.vehicle_id, valuation_date: today, value: loggedValue, source: loggedSource }).select();
            write.then(({ data: histData, error: histError }) => {
              if (histError || !histData?.[0]) return;
              const row = histData[0];
              setHistories(prev => existingEntry
                ? prev.map(h => h.id === row.id ? row : h)
                : [...prev, row]);
            });
          });
      });
  }, [selectedVal?.id, result?.estimatedValue, selectedVal?.manual_market_value, comparables.length, compsOk]);

  const chartData = [
    ...(selectedVal?.purchase_date && selectedVal?.purchase_price ? [{ date: format(parseISO(selectedVal.purchase_date), 'MMM yyyy'), value: Number(selectedVal.purchase_price) }] : []),
    ...selectedHist.map(h => ({ date: format(parseISO(h.valuation_date), 'MMM yyyy'), value: Number(h.value) })),
    ...(result?.estimatedValue ? [{ date: 'Now', value: Number(result.estimatedValue) }] : []),
  ];

  const reportChartData = reports.filter(r => r.vehicle_id === selectedVehicleId).map(r => ({ date: format(parseISO(r.report_date), 'dd MMM yy'), value: Number(r.estimated_value) })).reverse();

  const radarData = collectorScore ? Object.entries(collectorScore.breakdown).map(([key, val]) => ({ subject: key, score: val.score, max: val.max, pct: Math.round((val.score / val.max) * 100) })) : [];

  const totalPurchase = valuations.reduce((s, v) => s + Number(v.purchase_price || 0), 0);
  const totalCurrent  = valuations.reduce((s, v) => s + Number(v.current_value || 0), 0);
  const totalGainLoss = totalCurrent - totalPurchase;




  const generateMarketIntelligence = async () => {
    if (!selectedVehicle) { toast.error('Select a vehicle first'); return; }
    setReportLoading(true); setReportError(null); setMarketReport(null);
    try {
      const { data, error } = await supabase.functions.invoke('generate-market-intelligence', {
        body: {
          vehicle: selectedVehicle,
          valuation: selectedVal,
          comparables,
          collectorScore,
        },
      });
      if (error || !data?.success) {
        setReportError(error?.message || data?.error || 'Failed to generate report');
        setReportLoading(false); return;
      }

      const report = data.report;
      setMarketReport(report);

      // Save to database
      const { error: saveError } = await supabase.from('market_intelligence_reports').insert({
        vehicle_id:               selectedVehicleId,
        user_id:                  (await supabase.auth.getUser()).data.user?.id,
        investment_verdict:       report.investment_verdict || null,
        verdict_reasoning:        report.verdict_reasoning || null,
        key_value_drivers:        report.key_value_drivers || [],
        best_season_to_sell:      report.best_season_to_sell || null,
        best_season_reasoning:    report.best_season_reasoning || null,
        recommended_holding_years:report.recommended_holding_years || null,
        holding_reasoning:        report.holding_reasoning || null,
        next_peak_year:           report.next_peak_year || null,
        peak_year_reasoning:      report.peak_year_reasoning || null,
        forecast_year_1:          report.appreciation_forecast?.year_1 || null,
        forecast_year_3:          report.appreciation_forecast?.year_3 || null,
        forecast_year_5:          report.appreciation_forecast?.year_5 || null,
        forecast_year_10:         report.appreciation_forecast?.year_10 || null,
        forecast_reasoning:       report.forecast_reasoning || null,
        price_trend:              report.price_trend || null,
        narrative_overview:       report.market_narrative?.overview || null,
        narrative_demand_drivers: report.market_narrative?.demand_drivers || null,
        narrative_regional:       report.market_narrative?.regional_analysis || null,
        narrative_investment_outlook: report.market_narrative?.investment_outlook || null,
        narrative_risk_factors:   report.market_narrative?.risk_factors || null,
        risk_factors:             report.risk_factors || [],
        comparables_used:         data.comparablesUsed || 0,
        web_sources_used:         data.webSourcesUsed || 0,
        collector_score:          collectorScore?.total || null,
      });

      if (saveError) {
        console.error('Failed to save report:', saveError.message);
      }

      // Auto-save market timing fields to vehicle_valuations
      if (selectedVal) {
        await supabase.from('vehicle_valuations').update({
          best_season_to_sell:       report.best_season_to_sell || null,
          recommended_holding_years: report.recommended_holding_years || null,
          next_peak_year:            report.next_peak_year || null,
          updated_at:                new Date().toISOString(),
        }).eq('id', selectedVal.id);
      }

      toast.success('Market Intelligence report generated and saved!');
    } catch (err) {
      setReportError(err.message);
    }
    setReportLoading(false);
    fetchSavedReports();
  };

  const deleteComp = async (id) => {
    if (!window.confirm('Delete this comparable?')) return;
    await supabase.from('comparable_vehicles').delete().eq('id', id);
    toast.success('Deleted'); fetchComparables();
  };

  const saveReport = async () => {
    if (!result || !selectedVehicleId) { toast.error('Calculate a valuation first'); return; }
    setSaving(true);
    const { error } = await supabase.from('valuation_reports').insert({ vehicle_id: selectedVehicleId, report_date: format(new Date(), 'yyyy-MM-dd'), avg_comparable_price: result.median, estimated_value: result.estimatedValue, purchase_price: purchasePrice, appreciation_amount: appreciationAmount, appreciation_pct: appreciationPct, confidence_score: result.confidence, condition_factor: result.factors.condF, mileage_factor: result.factors.mileF, originality_factor: result.factors.origF, service_factor: result.factors.svcF, rarity_factor: result.factors.rarF, demand_factor: result.factors.demF, market_summary: buildMarketSummary(marketReport) }).select();
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    toast.success('Report saved!'); fetchData(); setSaving(false);
  };

  const trendIcon  = { Appreciating: TrendingUp, Stable: Minus, Depreciating: TrendingDown };
  const trendColor = { Appreciating: '#22c55e', Stable: '#4f8ef7', Depreciating: '#ef4444' };

  if (loading) return <div className="loading-center"><div className="spinner" /></div>;

  if (!canUseAI && !isTrialing) {
    return (
      <div className="page">
        {isNative && <PhoneHero sub="Here's your car intelligence today" />}
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: '60vh', textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ width: 80, height: 80, borderRadius: '50%', background: 'linear-gradient(135deg, rgba(79,142,247,0.15), rgba(124,58,237,0.15))', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 24px' }}>
            <Brain size={36} style={{ color: 'var(--accent)', opacity: 0.7 }} />
          </div>
          <h2 style={{ fontSize: 24, fontWeight: 800, marginBottom: 12 }}>Car Intelligence is a premium feature</h2>
          <p style={{ color: 'var(--text3)', fontSize: 15, maxWidth: 420, marginBottom: 32, lineHeight: 1.7 }}>
            {isNative
              ? 'AI-powered valuations, the Collector Score and real market comparables are not included in your current plan.'
              : <>Upgrade to the <strong style={{ color: '#4f8ef7' }}>Enthusiast</strong>, <strong style={{ color: '#a855f7' }}>Collector</strong>, or <strong style={{ color: '#c9a84c' }}>Curator</strong> plan to access AI-powered valuations, the Collector Score, and real market comparables.</>}
          </p>
          {!isNative && (
            <>
              <button
                className="btn btn-primary"
                style={{ padding: '14px 32px', fontSize: 15 }}
                onClick={() => navigate('/settings')}
              >
                View Plans & Upgrade →
              </button>
              <div style={{ marginTop: 16, fontSize: 14, color: 'var(--text2)' }}>
                Plans start from €19/month · Cancel anytime
              </div>
            </>
          )}
        </div>
      </div>
    );
  }

  // ── Tabs config ───────────────────────────────────────────────────────────
  // Market Intelligence is a Curator-only feature (trial users get a preview,
  // same as every other premium feature during their trial window).
  const marketIntelAccess = plan === 'curator' || isTrialing;
  const TABS = [
    { key: 'collector',   label: 'Collector Score', grad: ['#eab308','#f97316'],  icon: <Star size={16}/> },
    { key: 'comparables', label: `Comparables${compsLoading ? ' …' : comparables.length > 0 ? ` (${comparables.length})` : ''}`, grad: ['#22c55e','#06b6d4'], icon: <BarChart3 size={16}/> },
    { key: 'intelligence', label: `Market Intelligence${marketIntelAccess ? '' : ' 🔒'}`, grad: ['#c9a84c','#e8c97a'], icon: <TrendingUp size={16}/> },
    { key: 'description', label: 'Description', grad: ['#4f8ef7','#06b6d4'], icon: <FileText size={16}/> },
    { key: 'reports',      label: 'Reports',             grad: ['#a855f7','#ec4899'], icon: <Save size={16}/> },
  ];

  const carGrid = (
          <div style={{ display: 'grid', gridTemplateColumns: isNative ? 'repeat(2, minmax(0, 1fr))' : 'repeat(auto-fill, minmax(190px, 1fr))', gap: 12, maxHeight: isNative ? 'none' : 460, overflowY: isNative ? 'visible' : 'auto' }}>
            {vehicles.map((v, i) => {
              const val = valuations.find(vv => vv.vehicle_id === v.id);
              const score = val ? calcCollectorScore(val).total : null;
              // True return: what's actually in the car (purchase price + restoration +
              // every other expense, insurance, road tax, service and mechanical cost)
              // against what it's worth now — not just purchase price vs. market value.
              // Restoration/expense split matches Portfolio's invested-capital convention.
              const purchasePrice = val?.purchase_price ? Number(val.purchase_price) : null;
              const currentValue  = val?.current_value  ? Number(val.current_value)  : null;
              const vExpenses = expenses.filter(e => e.vehicle_id === v.id);
              const restorationSpend = vExpenses.filter(e => e.category === 'Restoration').reduce((s, e) => s + Number(e.amount || 0), 0);
              const otherCosts =
                vExpenses.filter(e => e.category !== 'Restoration').reduce((s, e) => s + Number(e.amount || 0), 0) +
                insuranceRecords.filter(e => e.vehicle_id === v.id).reduce((s, e) => s + Number(e.cost || 0), 0) +
                roadTaxRecords.filter(e => e.vehicle_id === v.id).reduce((s, e) => s + Number(e.amount_paid || 0), 0) +
                serviceRecords.filter(e => e.vehicle_id === v.id).reduce((s, e) => s + Number(e.cost || 0), 0) +
                mechanicalRecords.filter(e => e.vehicle_id === v.id).reduce((s, e) => s + Number(e.cost || 0), 0);
              const totalInvested = purchasePrice ? purchasePrice + restorationSpend + otherCosts : null;
              const gl = totalInvested && currentValue ? ((currentValue - totalInvested) / totalInvested) * 100 : null;
              const isSelected = selectedVehicleId === v.id;
              const grad = GRADIENTS[i % GRADIENTS.length];
              return (
                <div key={v.id} onClick={() => { setSelectedVehicleId(v.id); setActiveTab('collector'); setMarketReport(null); setReportError(null); setCarSheet(false); if (isNative) setShowVehicleList(false); }}
                  style={{ border: `2px solid ${isSelected ? grad[0] : 'var(--border)'}`, borderRadius: 12, overflow: 'hidden', cursor: 'pointer', background: isSelected ? `${grad[0]}14` : 'var(--surface2)', transition: 'all 0.15s' }}>
                  <div style={{ height: isNative ? 92 : 96, background: `linear-gradient(135deg, ${grad[0]}, ${grad[1]})`, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {(() => {
                      const photo = v.vehicle_photos?.find(p => p.is_primary) || v.vehicle_photos?.[0];
                      return photo ? <img src={photo.url} alt="" style={{ width: '100%', height: '100%', ...cropStyle(photo) }} /> : <Car size={26} style={{ color: 'white' }} />;
                    })()}
                  </div>
                  <div style={{ padding: 10 }}>
                    <div style={{ fontWeight: 700, fontSize: isNative ? 15 : 13, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{v.brand} {v.model}</div>
                    <div style={{ fontSize: isNative ? 12 : 10, color: 'var(--text3)', fontFamily: 'DM Mono,monospace', marginTop: 2 }}>{v.registration_number} · {v.year}</div>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 8 }}>
                      {score !== null
                        ? <span style={{ fontSize: isNative ? 13 : 11, fontWeight: 700, color: score >= 70 ? '#22c55e' : score >= 50 ? '#4f8ef7' : '#eab308' }}>{score}/100</span>
                        : <span style={{ fontSize: 10, color: 'var(--text3)' }}>No score</span>}
                      {gl !== null
                        ? <span title="Net of purchase price, restoration and running costs vs. current value" style={{ fontSize: isNative ? 12 : 10, fontWeight: 600, color: gl >= 0 ? '#22c55e' : '#ef4444' }}>{gl >= 0 ? '+' : ''}{gl.toFixed(1)}%</span>
                        : <span style={{ fontSize: 10, color: 'var(--text3)' }}>No data</span>}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
  );

  return (
    <div className="page">
      {isNative && (
        <PhoneHero
          sub="Here's your car intelligence today"
          chips={selectedVehicleId ? [] : [`${vehicles.length} car${vehicles.length !== 1 ? 's' : ''}`, `${valuations.length} valued`]}
        />
      )}
      {!isNative && <div className="page-header">
        <div>
          <div className="page-title">Car Intelligence</div>
          <div className="page-subtitle">Collector score · AI valuation engine · Market analysis</div>
        </div>
      </div>}

      {/* Fleet summary (phone: only on the car picker) */}
      {(!isNative || !selectedVehicleId) && <div className="stats-grid" style={{ marginBottom: 24 }}>
        {[
          { label: 'Total Purchase Value',  value: formatCurrency(totalPurchase), grad: ['#4f8ef7','#7c3aed'] },
          { label: 'Total Estimated Value', value: formatCurrency(totalCurrent),  grad: ['#22c55e','#06b6d4'] },
          { label: totalGainLoss >= 0 ? 'Fleet Appreciation' : 'Fleet Depreciation', value: `${totalGainLoss >= 0 ? '+' : ''}${formatCurrency(Math.abs(totalGainLoss))}`, grad: totalGainLoss >= 0 ? ['#22c55e','#16a34a'] : ['#ef4444','#dc2626'] },
          { label: 'Vehicles Tracked',      value: valuations.length,             grad: ['#a855f7','#ec4899'] },
        ].map((item, i) => (
          <div key={i} className="stat-card" style={{ position: 'relative', overflow: 'hidden' }}>
            
            <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: -0.5, background: `linear-gradient(135deg, ${item.grad[0]}, ${item.grad[1]})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{item.value}</div>
            <div className="stat-label" style={{ marginTop: 4 }}>{item.label}</div>
          </div>
        ))}
      </div>}


      {/* ── Vehicle selector — full width on mobile ── */}
      {/* ── Vehicle selector — card grid (web) ── */}
      {!isNative && <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showVehicleList ? 14 : 0, cursor: 'pointer' }}
          onClick={() => setShowVehicleList(v => !v)}>
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            {selectedVehicle ? `Selected: ${selectedVehicle.brand} ${selectedVehicle.model}` : 'Select a vehicle'}
          </span>
          {showVehicleList ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
        {showVehicleList && carGrid}
      </div>}

      {/* Phone: choose a car (2 per row) */}
      {isNative && !selectedVehicleId && (
        <div className="ci-pick">
          <h2 className="ci-pick-title">Choose a car</h2>
          <p className="ci-pick-sub">Tap a car to see its collector score, comparables and market intelligence.</p>
          {carGrid}
        </div>
      )}

      {/* ── Right panel ── */}
      {!selectedVehicleId ? (
        isNative ? null : <div className="card" style={{ textAlign: 'center', padding: '60px 20px' }}>
          <Brain size={40} style={{ color: 'var(--accent)', margin: '0 auto 16px', opacity: 0.5 }} />
          <h3 style={{ fontWeight: 700, marginBottom: 6 }}>Select a vehicle</h3>
          <p style={{ color: 'var(--text3)', fontSize: 13 }}>Choose a vehicle above to view its full intelligence report.</p>
        </div>
      ) : (
        <>
          {isNative ? (
            <>
              <StickyBar className="ci-sticky">
                <div className="ci-bar">
                  <div className="ci-bar-thumb">
                    {(() => {
                      const photo = selectedVehicle?.vehicle_photos?.find(p => p.is_primary) || selectedVehicle?.vehicle_photos?.[0];
                      return photo ? <img src={photo.url} alt="" style={{ width: '100%', height: '100%', ...cropStyle(photo) }} /> : <Car size={22} style={{ color: 'white' }} />;
                    })()}
                  </div>
                  <div className="ci-bar-text">
                    <div className="ci-bar-name">{selectedVehicle?.brand} {selectedVehicle?.model}</div>
                    <div className="ci-bar-sub">{[selectedVehicle?.year, selectedVehicle?.registration_number].filter(Boolean).join(' · ')}{collectorScore ? `  ·  Score ${collectorScore.total}/100` : ''}</div>
                  </div>
                  <button className="ci-change" onClick={() => setCarSheet(true)}>Change car <ChevronDown size={16} /></button>
                </div>
                <ScrollTabs>
                  {TABS.map(t => {
                    const isActive = activeTab === t.key;
                    return (
                      <button key={t.key} className={`ci-tab ${isActive ? 'is-active' : ''}`}
                        style={{ '--g1': t.grad[0], '--g2': t.grad[1] }}
                        ref={el => { if (el && isActive && el.scrollIntoView) el.scrollIntoView({ inline: 'center', block: 'nearest' }); }}
                        onClick={() => setActiveTab(t.key)}>
                        {t.icon} {t.label}
                      </button>
                    );
                  })}
                </ScrollTabs>
              </StickyBar>
              <div className="ci-meta">
                <div className="ci-meta-badges">
                  {selectedVal?.condition_grade && <span className="ci-badge" style={{ background: '#22c55e22', color: '#22c55e', borderColor: '#22c55e44' }}>⭐ {selectedVal.condition_grade}</span>}
                  {selectedVal?.market_trend && (() => { const TI = trendIcon[selectedVal.market_trend]; return TI ? <span className="ci-badge" style={{ background: `${trendColor[selectedVal.market_trend]}22`, color: trendColor[selectedVal.market_trend], borderColor: `${trendColor[selectedVal.market_trend]}44` }}><TI size={13}/> {selectedVal.market_trend}</span> : null; })()}
                  {selectedVal?.category && <span className="ci-badge" style={{ background: 'var(--accent-glow)', color: 'var(--accent)', borderColor: 'var(--accent)' }}>{selectedVal.category}</span>}
                </div>
                <div className="ci-meta-actions">
                  <button className="btn btn-ghost btn-sm" onClick={() => { setEditValuation(selectedVal); setShowValuationModal(true); }}><Edit2 size={14}/> {selectedVal ? 'Edit Data' : 'Add Data'}</button>
                  {selectedVal && <button className="btn btn-primary btn-sm" onClick={() => setShowHistoryModal(true)}><Plus size={14}/> Log Value</button>}
                </div>
              </div>
              {carSheet && createPortal(
                <div className="m-sheet-scrim" onClick={() => setCarSheet(false)}>
                  <div className="m-sheet ci-sheet" onClick={e => e.stopPropagation()}>
                    <div className="m-sheet-grip" />
                    <div className="m-sheet-head">
                      <div className="m-sheet-title">Choose a car</div>
                      <button className="m-iconbtn" aria-label="Close" onClick={() => setCarSheet(false)}><X size={22} /></button>
                    </div>
                    <div className="ci-sheet-body">{carGrid}</div>
                  </div>
                </div>,
                document.body
              )}
            </>
          ) : (
            <>
          {/* Vehicle header */}
          <div className="card" style={{ marginBottom: 16, position: 'relative', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, flexWrap: 'wrap' }}>
              <div style={{ width: 56, height: 56, borderRadius: 12, overflow: 'hidden', background: 'linear-gradient(135deg, #4f8ef7, #7c3aed)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                {(() => {
                  const photo = selectedVehicle?.vehicle_photos?.find(p => p.is_primary) || selectedVehicle?.vehicle_photos?.[0];
                  return photo ? <img src={photo.url} alt="" style={{ width: '100%', height: '100%', ...cropStyle(photo) }} /> : <Car size={24} style={{ color: 'white' }} />;
                })()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontFamily: 'DM Mono,monospace', fontSize: 12, color: 'var(--accent)' }}>{selectedVehicle?.registration_number}</div>
                <div style={{ fontSize: 18, fontWeight: 800, lineHeight: 1.2 }}>{selectedVehicle?.brand} {selectedVehicle?.model} <span style={{ color: 'var(--text3)', fontWeight: 400, fontSize: 14 }}>· {selectedVehicle?.year}</span></div>
                <div style={{ display: 'flex', gap: 6, marginTop: 6, flexWrap: 'wrap' }}>
                  {selectedVal?.condition_grade && <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: '#22c55e22', color: '#22c55e', border: '1px solid #22c55e44' }}>⭐ {selectedVal.condition_grade}</span>}
                  {selectedVal?.market_trend && (() => { const TI = trendIcon[selectedVal.market_trend]; return TI ? <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 600, background: `${trendColor[selectedVal.market_trend]}22`, color: trendColor[selectedVal.market_trend], border: `1px solid ${trendColor[selectedVal.market_trend]}44`, display: 'inline-flex', alignItems: 'center', gap: 4 }}><TI size={11}/>{selectedVal.market_trend}</span> : null; })()}
                  {selectedVal?.category && <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, background: 'var(--accent-glow)', color: 'var(--accent)', border: '1px solid var(--accent)44' }}>{selectedVal.category}</span>}
                  {collectorScore && <span style={{ padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: 'linear-gradient(135deg, #4f8ef722, #7c3aed22)', color: 'var(--accent)', border: '1px solid var(--accent)44' }}>Score: {collectorScore.total}/100</span>}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button className="btn btn-ghost btn-sm" onClick={() => { setEditValuation(selectedVal); setShowValuationModal(true); }}><Edit2 size={13}/> {selectedVal ? 'Edit Data' : 'Add Data'}</button>
                {selectedVal && <button className="btn btn-primary btn-sm" onClick={() => setShowHistoryModal(true)}><Plus size={13}/> Log Value</button>}
              </div>
            </div>
          </div>

          {/* Tabs — horizontally scrollable on mobile */}
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, overflowX: 'auto', paddingBottom: 4, scrollbarWidth: 'none' }}>
            {TABS.map(t => {
              const isActive = activeTab === t.key;
              return (
                <button key={t.key} onClick={() => setActiveTab(t.key)} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderRadius: 12, cursor: 'pointer', fontFamily: 'DM Sans, sans-serif', fontSize: 13, fontWeight: 600, transition: 'all 0.2s', border: 'none', whiteSpace: 'nowrap', flexShrink: 0, background: isActive ? `linear-gradient(135deg, ${t.grad[0]}, ${t.grad[1]})` : 'var(--surface)', color: isActive ? 'white' : 'var(--text2)', boxShadow: isActive ? `0 8px 20px ${t.grad[0]}44` : '0 2px 4px rgba(0,0,0,0.2)', border: isActive ? 'none' : '1px solid var(--border)' }}>
                  {t.icon} {t.label}
                </button>
              );
            })}
          </div>

            </>
          )}

          {/* ── Collector Score Tab ── */}
          {activeTab === 'collector' && (
            <div style={{ position: 'relative' }}>
              {/* While comparables load, the market value, gain and confidence figures on
                  this tab are either blank or still showing the PREVIOUS vehicle's numbers.
                  Blocking the tab prevents reading a figure that is about to change. */}
              {compsLoading && (
                <div style={{
                  position: 'absolute', inset: 0, zIndex: 20,
                  background: 'rgba(13,27,62,0.72)', backdropFilter: 'blur(2px)',
                  borderRadius: 'var(--radius)',
                }}>
                  {/* The overlay covers the WHOLE tab, which is far taller than the
                      screen — centring within it would push the spinner off-view.
                      Sticky keeps it in the visible area instead. */}
                  <div style={{
                    position: 'sticky', top: '30vh',
                    display: 'flex', flexDirection: 'column',
                    alignItems: 'center', gap: 14, padding: '0 20px',
                  }}>
                    <div className="spinner" />
                    <div style={{ fontSize: 15, fontWeight: 600 }}>Searching the market database…</div>
                    <div style={{ fontSize: 13, color: 'var(--text3)', maxWidth: 340, textAlign: 'center', lineHeight: 1.5 }}>
                      Matching against 438,000 sold listings to value this vehicle.
                      This can take a few seconds.
                    </div>
                  </div>
                </div>
              )}
              {!selectedVal ? (
                <div className="card" style={{ textAlign: 'center', padding: '40px 20px' }}>
                  <Star size={32} style={{ color: 'var(--text3)', margin: '0 auto 12px', opacity: 0.3 }} />
                  <h3 style={{ fontWeight: 600, marginBottom: 6 }}>No valuation data yet</h3>
                  <p style={{ color: 'var(--text3)', fontSize: 13, marginBottom: 16 }}>Add vehicle data to calculate the collector score.</p>
                  <button className="btn btn-primary" onClick={() => { setEditValuation(null); setShowValuationModal(true); }}><Plus size={14}/> Add Vehicle Data</button>
                </div>
              ) : (
                <>
                  <MileageMismatchNotice thisKm={selectedVal.mileage_score} otherKm={selectedVehicle?.mileage}
                    thisLabel="The Collector Score’s Current Mileage" otherLabel="the Edit Vehicle card"
                    style={{ marginTop: 0, marginBottom: 16 }} />

                  {/* Key metrics — 2 cols on mobile */}
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10, marginBottom: 16 }}>
                    {[
                      { label: 'Purchase Price', value: formatCurrency(selectedVal.purchase_price), grad: ['#4f8ef7','#7c3aed'] },
                      { label: valueSource === 'owner' ? 'Your Estimated Value' : 'Estimated Market Value',
                        value: formatCurrency(marketValue), grad: ['#22c55e','#06b6d4'],
                        warn: valueSource === 'owner' ? 'your own figure — not from comparables'
                            : valueSource === 'stored' ? 'no comparables support this'
                            : (marketValue == null && comparables.length < 3 ? 'not enough comparables to value this' : false) },
                      { label: gainLoss !== null && gainLoss < 0 ? 'Total Loss' : 'Total Gain',
                        value: gainLoss === null ? '—' : `${gainLoss >= 0 ? '+' : ''}${formatCurrency(Math.abs(gainLoss))}`,
                        grad: (gainLoss || 0) >= 0 ? ['#22c55e','#06b6d4'] : ['#ef4444','#dc2626'] },
                      { label: 'Yearly Rate', value: yearlyRate !== null ? `${yearlyRate >= 0 ? '+' : ''}${yearlyRate.toFixed(1)}%/yr` : '—',
                        grad: (yearlyRate || 0) >= 0 ? ['#22c55e','#06b6d4'] : ['#ef4444','#dc2626'],
                        warn: yearlyRate === null && selectedVal?.purchase_date && yearsOwned < MIN_YEARS_FOR_YEARLY_RATE
                          ? 'owned under 6 months — too early to annualize' : false },
                    ].map((item, i) => (
                      <div key={i} style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px', position: 'relative', overflow: 'hidden' }}>
                        <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{item.label}</div>
                        <div style={{ fontSize: 14, fontWeight: 800, background: `linear-gradient(135deg, ${item.grad[0]}, ${item.grad[1]})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{item.value}</div>
                        {item.warn && (
                          <div style={{ fontSize: 10, color: '#f97316', marginTop: 4, lineHeight: 1.35 }}>
                            ⚠ {typeof item.warn === 'string' ? item.warn : 'No comparables match — not supported by market data'}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Collector Score — stacked on mobile */}
                  <div className="card" style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 16 }}>Collector Score</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap' }}>
                      <ScoreRing score={collectorScore?.total || 0} />
                      <div style={{ flex: 1, minWidth: 200 }}>
                        {collectorScore && Object.entries(collectorScore.breakdown).map(([key, val]) => (
                          <div key={key} style={{ marginBottom: 8 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, marginBottom: 3 }}>
                              <span style={{ color: 'var(--text2)' }}>{key}</span>
                              <span style={{ fontWeight: 600 }}>{val.score}/{val.max}</span>
                            </div>
                            <div style={{ height: 4, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${(val.score / val.max) * 100}%`, background: 'linear-gradient(90deg, #4f8ef7, #7c3aed)', borderRadius: 2 }} />
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Score Radar */}
                  <div className="card" style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>Score Radar</div>
                    <ResponsiveContainer width="100%" height={220}>
                      <RadarChart data={radarData}>
                        <PolarGrid stroke="var(--border)" />
                        <PolarAngleAxis dataKey="subject" tick={{ fill: 'var(--text3)', fontSize: 10 }} />
                        <PolarRadiusAxis angle={30} domain={[0, 100]} tick={false} axisLine={false} />
                        <Radar name="Score" dataKey="pct" stroke="#4f8ef7" fill="#4f8ef7" fillOpacity={0.25} strokeWidth={2} />
                      </RadarChart>
                    </ResponsiveContainer>
                  </div>

                  {/* Intelligence Scores */}
                  {(investmentScore !== null || liquidityScore !== null || auctionConfidence !== null) && (
                    <div className="card" style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>Intelligence Scores</div>
                      <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>AI-powered analysis of investment potential, market liquidity, and data confidence</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>

                        {/* Investment Score */}
                        {investmentScore !== null && (() => {
                          const color = investmentScore >= 75 ? '#22c55e' : investmentScore >= 50 ? '#4f8ef7' : investmentScore >= 30 ? '#eab308' : '#ef4444';
                          const label = investmentScore >= 75 ? 'Strong Hold' : investmentScore >= 50 ? 'Hold' : investmentScore >= 30 ? 'Consider Selling' : 'Sell';
                          return (
                            <div style={{ background: `${color}0d`, border: `1px solid ${color}30`, borderRadius: 14, padding: '18px 16px', position: 'relative', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle, ${color}25, transparent)`, filter: 'blur(12px)', pointerEvents: 'none' }} />
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color, marginBottom: 8 }}>Investment Score</div>
                              <div style={{ fontSize: 36, fontWeight: 900, color, lineHeight: 1, marginBottom: 4 }}>{investmentScore}</div>
                              <div style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 12 }}>{label}</div>
                              <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${investmentScore}%`, background: color, borderRadius: 2, transition: 'width 0.6s ease' }} />
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 8, lineHeight: 1.5 }}>
                                Based on appreciation rate, rarity, provenance and market trend
                              </div>
                            </div>
                          );
                        })()}

                        {/* Liquidity Score */}
                        {liquidityScore !== null && (() => {
                          const color = liquidityScore >= 75 ? '#06b6d4' : liquidityScore >= 50 ? '#4f8ef7' : liquidityScore >= 30 ? '#eab308' : '#ef4444';
                          const label = liquidityScore >= 75 ? 'Highly Liquid' : liquidityScore >= 50 ? 'Good Market' : liquidityScore >= 30 ? 'Thin Market' : 'Illiquid';
                          return (
                            <div style={{ background: `${color}0d`, border: `1px solid ${color}30`, borderRadius: 14, padding: '18px 16px', position: 'relative', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle, ${color}25, transparent)`, filter: 'blur(12px)', pointerEvents: 'none' }} />
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color, marginBottom: 8 }}>Liquidity Score</div>
                              <div style={{ fontSize: 36, fontWeight: 900, color, lineHeight: 1, marginBottom: 4 }}>{liquidityScore}</div>
                              <div style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 12 }}>{label}</div>
                              <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${liquidityScore}%`, background: color, borderRadius: 2, transition: 'width 0.6s ease' }} />
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 8, lineHeight: 1.5 }}>
                                Based on comparable volume, sold prices and market demand
                              </div>
                            </div>
                          );
                        })()}

                        {/* Auction Confidence */}
                        {auctionConfidence !== null && (() => {
                          const color = auctionConfidence >= 75 ? '#22c55e' : auctionConfidence >= 50 ? '#a855f7' : auctionConfidence >= 30 ? '#eab308' : '#ef4444';
                          const label = auctionConfidence >= 75 ? 'High Confidence' : auctionConfidence >= 50 ? 'Good Data' : auctionConfidence >= 30 ? 'Limited Data' : 'Sparse Data';
                          return (
                            <div style={{ background: `${color}0d`, border: `1px solid ${color}30`, borderRadius: 14, padding: '18px 16px', position: 'relative', overflow: 'hidden' }}>
                              <div style={{ position: 'absolute', top: -20, right: -20, width: 80, height: 80, borderRadius: '50%', background: `radial-gradient(circle, ${color}25, transparent)`, filter: 'blur(12px)', pointerEvents: 'none' }} />
                              <div style={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, color, marginBottom: 8 }}>Auction Confidence</div>
                              <div style={{ fontSize: 36, fontWeight: 900, color, lineHeight: 1, marginBottom: 4 }}>{auctionConfidence}</div>
                              <div style={{ fontSize: 11, color, fontWeight: 600, marginBottom: 12 }}>{label}</div>
                              <div style={{ height: 4, background: 'rgba(255,255,255,0.08)', borderRadius: 2, overflow: 'hidden' }}>
                                <div style={{ height: '100%', width: `${auctionConfidence}%`, background: color, borderRadius: 2, transition: 'width 0.6s ease' }} />
                              </div>
                              <div style={{ fontSize: 10, color: 'var(--text3)', marginTop: 8, lineHeight: 1.5 }}>
                                Based on sold ratio, reserve met rate and data recency
                              </div>
                            </div>
                          );
                        })()}

                      </div>
                    </div>
                  )}

                  {/* Estimated Market Value */}
                  {result && (
                    <div className="card" style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>Estimated Market Value</div>
                      <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>
                        Based on {comparables.length} comparables and Collector Score {collectorScore?.total || 0}/100
                      </div>

                      {/* Estimated value — full width */}
                      <div style={{ background: 'linear-gradient(135deg, rgba(34,197,94,0.1), rgba(6,182,212,0.05))', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 12, padding: '20px', position: 'relative', overflow: 'hidden', marginBottom: 12 }}>
                        <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 4 }}>ESTIMATED VALUE</div>
                        <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: -1, background: 'linear-gradient(135deg, #22c55e, #06b6d4)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
                          {formatCurrency(result.estimatedValue)}
                        </div>
                        <div style={{ fontSize: 13, color: 'var(--text2)', marginTop: 8 }}>
                          Median comparable {formatCurrency(result.median)} × {result.totalMultiplier.toFixed(2)} adjustment
                          {result.clamped ? ' (capped to the observed market range)' : ''}
                        </div>
                        <div style={{ fontSize: 11, color: result.usingSoldOnly ? '#22c55e' : '#eab308', marginTop: 4, fontWeight: 600 }}>
                          {result.usingSoldOnly
                            ? `✓ Based on ${result.soldCount} sold price${result.soldCount !== 1 ? 's' : ''}`
                            : `⚠ Based on asking prices (only ${result.soldCount} sold record${result.soldCount !== 1 ? 's' : ''} available)`}
                        </div>
                      </div>

                      {/* Valuation adjustments */}
                      <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 12, padding: '14px 16px', marginBottom: 16 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 10 }}>How this value was calculated</div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: 'var(--text2)', marginBottom: 10 }}>
                          <span>Median of {result.basisCount} {result.usingSoldOnly ? 'sold' : 'listed'} comparables</span>
                          <span style={{ fontWeight: 700, color: 'var(--text)' }}>{formatCurrency(result.median)}</span>
                        </div>
                        {[
                          { label: 'Condition',       value: result.factors.condF, note: selectedVal?.condition_grade || 'not recorded' },
                          { label: 'Mileage',         value: result.factors.mileF, note: result.medianCompKm ? `${Math.round(result.vehicleKm || 0).toLocaleString()} vs ${Math.round(result.medianCompKm).toLocaleString()} km typical` : 'not recorded' },
                          { label: 'Originality',     value: result.factors.origF, note: '' },
                          { label: 'Service history', value: result.factors.svcF,  note: selectedVal?.service_history_status || 'not recorded' },
                          { label: 'Rarity',          value: result.factors.rarF,  note: '' },
                          { label: 'Restoration',     value: result.factors.restF,
                            note: selectedVal?.restoration_status === 'Restored' && selectedVal?.frame_off_restoration ? 'Restored (frame-off)'
                              : selectedVal?.restoration_status === 'Restored' && selectedVal?.professional_restoration ? 'Restored (professional)'
                              : selectedVal?.restoration_status || 'not recorded' },
                          { label: 'Transmission',    value: result.factors.transF, note: selectedVal?.transmission_type || 'not recorded' },
                          { label: 'Market demand',   value: result.factors.demF,  note: selectedVal?.market_trend || 'not recorded' },
                          { label: 'Modifications',   value: result.factors.modF,  note: selectedVal?.modification_impact || 'not recorded' },
                          { label: 'Category / segment', value: result.factors.catF, note: selectedVal?.category_multiplier ? 'manual override' : 'none set' },
                        ].map(f => {
                          const c = f.value > 1.001 ? '#22c55e' : f.value < 0.999 ? '#ef4444' : 'var(--text3)';
                          return (
                            <div key={f.label} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                              <div style={{ fontSize: 12, color: 'var(--text2)', width: 110, flexShrink: 0 }}>{f.label}</div>
                              <div style={{ flex: 1, fontSize: 11, color: 'var(--text3)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{f.note}</div>
                              <div style={{ fontSize: 12, fontWeight: 700, color: c, width: 52, textAlign: 'right' }}>×{f.value.toFixed(2)}</div>
                            </div>
                          );
                        })}
                        <div style={{ borderTop: '1px solid var(--border)', marginTop: 8, paddingTop: 8, display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                          <span style={{ color: 'var(--text2)' }}>Combined adjustment</span>
                          <span style={{ fontWeight: 800, color: result.totalMultiplier >= 1 ? '#22c55e' : '#ef4444' }}>×{result.totalMultiplier.toFixed(3)}</span>
                        </div>
                        {collectorScore && collectorScore.completeness < 100 && (
                          <div style={{ fontSize: 11, color: '#f97316', marginTop: 8 }}>
                            ⚠ Vehicle data {collectorScore.completeness}% complete — unrecorded factors are treated as average. Add data for a sharper estimate.
                          </div>
                        )}
                      </div>

                      {/* Listing stats — full-width row underneath */}
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
                        {[
                          { label: 'Lowest (All Listings)', value: formatCurrency(result.displayMin), color: '#ef4444' },
                          { label: 'Average (All Listings)', value: formatCurrency(result.displayAvg), color: '#4f8ef7' },
                          { label: 'Highest (All Listings)', value: formatCurrency(result.displayMax), color: '#22c55e' },
                          { label: 'Comparables Used', value: `${comparables.length} records (${result.soldCount} sold)`, color: 'var(--text2)' },
                        ].map((item, i) => (
                          <div key={i} style={{ background: 'var(--surface2)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
                            <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{item.label}</div>
                            <div style={{ fontSize: 14, fontWeight: 700, color: item.color }}>{item.value}</div>
                          </div>
                        ))}
                      </div>

                      <div style={{ marginBottom: 8 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontSize: 12, color: 'var(--text2)' }}>Estimate Confidence</span>
                          <span style={{ fontSize: 12, fontWeight: 600, color: result.confidence >= 70 ? '#22c55e' : result.confidence >= 40 ? '#eab308' : '#ef4444' }}>{result.confidence}%</span>
                        </div>
                        <div style={{ height: 6, background: 'var(--surface2)', borderRadius: 3, overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${result.confidence}%`, background: result.confidence >= 70 ? 'linear-gradient(90deg, #22c55e, #06b6d4)' : result.confidence >= 40 ? '#eab308' : '#ef4444', borderRadius: 3, transition: 'width 0.6s ease' }} />
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                          {result.confidence < 40 ? '⚠️ Add more comparables to improve accuracy' : result.confidence < 70 ? '📊 Good estimate — more comparables will improve accuracy' : '✅ High confidence estimate'}
                        </div>
                      </div>

                      {/* Save this valuation as a dated report */}
                      <button className="btn btn-primary" style={{ width: '100%', justifyContent: 'center', marginTop: 16 }} onClick={saveReport} disabled={saving}>
                        <Save size={14}/> {saving ? 'Saving…' : 'Save Valuation Report'}
                      </button>
                      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 8, textAlign: 'center' }}>
                        Saves today's estimate with its comparables and factor breakdown — view them in the Reports tab.
                      </div>
                    </div>
                  )}

                  {/* P&L */}
                  <div className="card" style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 16 }}>Ownership Profit & Loss</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10 }}>
                      {[
                        { label: 'Purchase Price', value: formatCurrency(selectedVal.purchase_price), grad: ['#4f8ef7','#7c3aed'] },
                        { label: 'Estimated Market Value', value: formatCurrency(result?.estimatedValue), grad: ['#22c55e','#06b6d4'] },
                        { label: 'Yearly Running Costs', value: formatCurrency(yearlyCosts), grad: ['#ef4444','#dc2626'] },
                        { label: 'Est. Total Running Costs', value: formatCurrency(yearlyCosts * Math.max(yearsOwned, 1)), grad: ['#f97316','#ef4444'] },
                        { label: 'Net Gain / Loss', value: netGain !== null ? `${netGain >= 0 ? '+' : ''}${formatCurrency(Math.abs(netGain))}` : '—', grad: (netGain || 0) >= 0 ? ['#22c55e','#06b6d4'] : ['#ef4444','#dc2626'] },
                      ].map((item, i) => (
                        <div key={i} style={{ background: 'var(--surface2)', borderRadius: 8, padding: '12px 14px', border: '1px solid var(--border)' }}>
                          <div style={{ fontSize: 10, color: 'var(--text3)', marginBottom: 4 }}>{item.label}</div>
                          <div style={{ fontSize: 13, fontWeight: 700, background: `linear-gradient(135deg, ${item.grad[0]}, ${item.grad[1]})`, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>{item.value}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Value history chart */}
                  <div className="card">
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                      <div style={{ fontWeight: 700, fontSize: 17 }}>Value History</div>
                      <button className="btn btn-primary btn-sm" onClick={() => setShowHistoryModal(true)}><Plus size={12}/> Log Value</button>
                    </div>
                    {chartData.length >= 2 ? (
                      <ResponsiveContainer width="100%" height={200}>
                        <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="vhGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor={(gainLoss || 0) >= 0 ? '#22c55e' : '#ef4444'} stopOpacity={0.3}/>
                              <stop offset="95%" stopColor={(gainLoss || 0) >= 0 ? '#22c55e' : '#ef4444'} stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                          <XAxis dataKey="date" tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false}/>
                          <YAxis tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `€${v}`}/>
                          <Tooltip content={<CustomTooltip/>}/>
                          <Area type="monotone" dataKey="value" stroke={(gainLoss || 0) >= 0 ? '#22c55e' : '#ef4444'} strokeWidth={3} fill="url(#vhGrad)" dot={{ fill: (gainLoss || 0) >= 0 ? '#22c55e' : '#ef4444', r: 5 }} activeDot={{ r: 8 }}/>
                        </AreaChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '24px 0', color: 'var(--text3)', fontSize: 13 }}>
                        <button className="btn btn-ghost btn-sm" onClick={() => setShowHistoryModal(true)}>+ Log a value entry to start the chart</button>
                      </div>
                    )}
                    {selectedHist.length > 0 && (
                      <div style={{ overflowX: 'auto', marginTop: 16 }}>
                        <table>
                          <thead><tr><th>Date</th><th>Value</th><th>Source</th><th>Notes</th><th></th></tr></thead>
                          <tbody>
                            {selectedHist.map(h => (
                              <tr key={h.id}>
                                <td>{formatDate(h.valuation_date)}</td>
                                <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{formatCurrency(h.value)}</td>
                                <td>{h.source || '—'}</td>
                                <td>{h.notes || '—'}</td>
                                <td><button className="btn btn-danger btn-sm" onClick={async () => { if (!window.confirm('Delete?')) return; await supabase.from('valuation_history').delete().eq('id', h.id); toast.success('Deleted'); fetchData(); }}>✕</button></td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          )}

          {/* ── Comparables Tab ── */}
          {activeTab === 'comparables' && (
            <div>
              {/* Header */}
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 4 }}>
                      {selectedVehicle?.brand} {selectedVehicle?.model} — Market Database
                    </div>
                    <div style={{ fontSize: 14, color: 'var(--text2)' }}>
                      {comparables.length} comparable{comparables.length !== 1 ? 's' : ''} from our shared global database
                    </div>
                    <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 6, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <span>{
                        compTier === 'taxonomy'         ? 'Matched on exact catalogue specification'
                      : compTier === 'normalised'       ? 'Matched on make · model · generation · body · variant · year (±5)'
                      : compTier === 'normalised-loose' ? 'Matched on make · model · generation · body · year (±5)'
                      : compTier === 'grouped'          ? 'Matched on make · model · year (±5)'
                      : 'Matched on make · model text · year (±5)'
                      }</span>
                      {hiddenOutliers > 0 && (
                        <span style={{ color: 'var(--text3)' }}>· {hiddenOutliers} unusually high sale{hiddenOutliers !== 1 ? 's' : ''} hidden (likely race/provenance)</span>
                      )}
                      {(() => {
                        const missing = [];
                        if (!selectedVehicle?.variant) missing.push('Variant');
                        if (!selectedVehicle?.engine_size) missing.push('Engine Size');
                        return missing.length ? (
                          <span style={{ color: '#f97316', fontWeight: 600 }}>
                            ⚠ Add {missing.join(' & ')} to this vehicle for precise matching
                          </span>
                        ) : null;
                      })()}
                    </div>
                  </div>
                </div>
              </div>

              {/* Sold-price summary + trend */}
              {comparables.length > 0 && (() => {
                const sold = comparables.filter(c => c._priceEUR > 0 && c.sold_price && Number(c.sold_price) > 0);
                if (sold.length === 0) return null;
                const prices = sold.map(c => c._priceEUR).sort((a, b) => a - b);
                const n = prices.length;
                const pct = (p) => { const i = (n - 1) * p, lo = Math.floor(i), hi = Math.ceil(i); return prices[lo] + (prices[hi] - prices[lo]) * (i - lo); };
                const low = prices[0], high = prices[n - 1], median = pct(0.5);
                const fmt = (v) => formatCurrency(v);
                const points = sold
                  .filter(c => c.sale_date)
                  .map(c => ({ ts: new Date(c.sale_date).getTime(), price: c._priceEUR }))
                  .filter(p => !isNaN(p.ts))
                  .sort((a, b) => a.ts - b.ts);
                const trend = (() => {
                  if (points.length < 4) return [];
                  const w = Math.max(3, Math.round(points.length * 0.15));
                  return points.map((pt, i) => {
                    const a = Math.max(0, i - Math.floor(w / 2));
                    const b = Math.min(points.length, i + Math.ceil(w / 2));
                    const slice = points.slice(a, b);
                    return { ts: pt.ts, price: slice.reduce((s, p) => s + p.price, 0) / slice.length };
                  });
                })();
                return (
                  <div className="card" style={{ marginBottom: 16 }}>
                    <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>Sold price analysis</div>
                    <div style={{ fontSize: 13, color: 'var(--text3)', marginBottom: 14 }}>
                      Based on {n} sold listing{n !== 1 ? 's' : ''}, converted to € at current rates
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: 10, marginBottom: 18 }}>
                      {[
                        { label: 'Comparables', value: comparables.length, color: 'var(--text)' },
                        { label: 'Lowest sold', value: fmt(low), color: '#ef4444' },
                        { label: 'Median sold', value: fmt(median), color: '#4f8ef7' },
                        { label: 'Highest sold', value: fmt(high), color: '#22c55e' },
                      ].map(s => (
                        <div key={s.label} style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 10, padding: '12px 14px' }}>
                          <div style={{ fontSize: 11, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>{s.label}</div>
                          <div style={{ fontSize: 18, fontWeight: 800, color: s.color }}>{s.value}</div>
                        </div>
                      ))}
                    </div>
                    {points.length >= 2 ? (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 10 }}>Previous Sales (€)</div>
                        <ResponsiveContainer width="100%" height={300}>
                          <ScatterChart margin={{ top: 8, right: 16, left: 8, bottom: 4 }}>
                            <CartesianGrid strokeDasharray="0" stroke="var(--border)" vertical={false} />
                            <XAxis type="number" dataKey="ts" domain={['dataMin', 'dataMax']}
                              tickFormatter={(t) => new Date(t).getFullYear()}
                              tick={{ fill: 'var(--text3)', fontSize: 11 }} />
                            <YAxis type="number" dataKey="price" width={60}
                              tickFormatter={(v) => Math.round(v).toLocaleString()}
                              tick={{ fill: 'var(--text3)', fontSize: 11 }}
                              label={{ value: 'Price (€)', angle: -90, position: 'insideLeft', fill: 'var(--text3)', fontSize: 11, style: { textAnchor: 'middle' } }} />
                            <Tooltip cursor={{ strokeDasharray: '3 3' }}
                              content={({ payload }) => {
                                if (!payload || !payload.length) return null;
                                const p = payload[0].payload;
                                return (
                                  <div style={{ background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 10px', fontSize: 12 }}>
                                    <div style={{ color: 'var(--text3)' }}>{new Date(p.ts).toLocaleDateString('en-GB')}</div>
                                    <div style={{ fontWeight: 700 }}>{fmt(p.price)}</div>
                                  </div>
                                );
                              }} />
                            <Scatter data={points} isAnimationActive={false}
                              shape={(props) => <circle cx={props.cx} cy={props.cy} r={4} fill="none" stroke="var(--text2)" strokeWidth={1.3} />} />
                            {trend.length > 0 && (
                              <Scatter data={trend} isAnimationActive={false}
                                line={{ stroke: '#4f8ef7', strokeWidth: 2.5, strokeDasharray: '6 6' }}
                                lineJointType="monotoneX" shape={() => <g />} />
                            )}
                          </ScatterChart>
                        </ResponsiveContainer>
                        <div style={{ fontSize: 12, color: 'var(--text3)', marginTop: 8 }}>Each circle is one sold result (in €); the dashed line is the price trend.</div>
                      </>
                    ) : (
                      <div style={{ fontSize: 13, color: 'var(--text3)' }}>Not enough dated sales to plot a trend yet.</div>
                    )}
                  </div>
                );
              })()}

              {/* Comparables database */}
              {compsLoading ? (
                <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
                  <div className="spinner" style={{ margin: '0 auto 16px' }} />
                  <h3 style={{ fontWeight: 600, marginBottom: 8 }}>Searching the market database…</h3>
                  <p style={{ color: 'var(--text3)', fontSize: 13.5, maxWidth: 400, margin: '0 auto' }}>
                    Matching against 438,000 sold listings. This can take a few seconds.
                  </p>
                </div>
              ) : comparables.length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(79,142,247,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <BarChart3 size={28} style={{ color: 'var(--accent)' }} />
                  </div>
                  <h3 style={{ fontWeight: 600, marginBottom: 8 }}>No comparables yet</h3>
                  <p style={{ color: 'var(--text3)', fontSize: 14, marginBottom: 20, maxWidth: 360, margin: '0 auto 20px' }}>
                    Comparables are matched from your shared market database. Import more sold results in Supabase to populate this vehicle.
                  </p>
                </div>
              ) : (() => {
                // Regions are no longer used — a single "Comparables" group
                const regionLabels = { all: 'Comparables' };
                const grouped = { all: comparables };


                return (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                    {/* Grouped by region */}
                    {Object.entries(grouped).map(([region, comps]) => (
                      <div key={region} className="card" style={{ padding: 0, overflow: 'hidden' }}>
                        <div style={{ padding: '12px 16px', background: 'linear-gradient(135deg, rgba(79,142,247,0.08), rgba(124,58,237,0.05))', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ fontWeight: 700, fontSize: 14 }}>{regionLabels[region] || region}</div>
                          <span style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'DM Mono, monospace' }}>{comps.length} record{comps.length !== 1 ? 's' : ''}</span>
                        </div>
                        <div style={{ padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                          {comps.map(c => (
                            <div key={c.id} style={{ display: 'flex', gap: 12, padding: '10px 12px', background: 'var(--surface2)', borderRadius: 10, border: `1px solid ${c.sold_price ? 'rgba(34,197,94,0.2)' : 'rgba(79,142,247,0.15)'}`, alignItems: 'flex-start' }}>
                              {c.photo_url ? (
                                <img src={c.photo_url} alt="" style={{ width: 64, height: 48, objectFit: 'cover', borderRadius: 6, flexShrink: 0, border: '1px solid var(--border)' }}
                                  onError={e => e.target.style.display = 'none'} />
                              ) : (
                                <div style={{ width: 64, height: 48, borderRadius: 6, background: 'var(--surface)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                  <Car size={20} style={{ color: 'var(--text3)', opacity: 0.4 }} />
                                </div>
                              )}
                              <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 4 }}>
                                  <span style={{ fontWeight: 700, fontSize: 13 }}>{c.year} {c.make} {c.model}</span>
                                  {c.variant && <span style={{ fontSize: 11, color: 'var(--text3)' }}>{c.variant}</span>}
                                  {c._matchPct != null && (
                                    <span
                                      title={c._matchFields && c._matchFields.length ? `Matches on: ${c._matchFields.join(', ')}` : 'Matched on make / model / year'}
                                      style={{ fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 20, color: '#fff', background: matchTier(c._matchPct).color }}>
                                      {matchTier(c._matchPct).label}
                                    </span>
                                  )}
                                  <span className={`badge badge-${c.sold_price ? 'green' : 'blue'}`} style={{ fontSize: 10 }}>
                                    {c.sold_price ? '✅ Sold' : '💬 Asking'}
                                  </span>
                                  {c.condition && <span className={`badge badge-${c.condition === 'Excellent' || c.condition === 'Concours' ? 'green' : c.condition === 'Poor' || c.condition === 'Fair' ? 'red' : 'gray'}`} style={{ fontSize: 10 }}>{c.condition}</span>}
                                </div>
                                <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', fontSize: 12, color: 'var(--text2)' }}>
                                  {c.mileage && <span>🛣 {formatMileage(c.mileage, { unit: c.mileage_unit || 'km' })}</span>}
                                  {c.country && <span>📍 {c.country === 'Global' ? 'USA' : c.country}</span>}
                                  {c.sale_date && <span>📅 {formatDate(c.sale_date)}</span>}
                                  {c.source_website && <span style={{ color: 'var(--text3)' }}>via {c.source_website}</span>}
                                </div>
                              </div>
                              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                                {c.sold_price && (
                                  <div style={{ fontSize: 15, fontWeight: 800, color: '#22c55e' }}>
                                    {c.currency && c.currency !== 'EUR' ? `${c.currency} ${Number(c.sold_price).toLocaleString()}` : formatCurrency(c.sold_price)}
                                  </div>
                                )}
                                {c.asking_price && !c.sold_price && (
                                  <div style={{ fontSize: 15, fontWeight: 800, color: '#4f8ef7' }}>
                                    {c.currency && c.currency !== 'EUR' ? `${c.currency} ${Number(c.asking_price).toLocaleString()}` : formatCurrency(c.asking_price)}
                                  </div>
                                )}
                              </div>
                              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, flexShrink: 0 }}>
                                {c.listing_url && (
                                  <a href={c.listing_url} target="_blank" rel="noreferrer" className="btn btn-ghost btn-sm" title="View listing">
                                    <ExternalLink size={11}/>
                                  </a>
                                )}
                                {role === 'owner' && (
                                  <>
                                    <button className="btn btn-danger btn-sm" onClick={async () => {
                                      if (!window.confirm('Delete this comparable?')) return;
                                      await supabase.from('comparables_shared').delete().eq('id', c.id);
                                      fetchComparables();
                                      toast.success('Deleted');
                                    }} title="Delete">
                                      <Trash2 size={11}/>
                                    </button>
                                  </>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })()}
            </div>
          )}

          {/* ── Market Intelligence Tab ── */}
          {activeTab === 'intelligence' && (marketIntelAccess ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

              {/* Generate button */}
              <div style={{ background: 'linear-gradient(135deg, rgba(201,168,76,0.1), rgba(232,201,122,0.05))', border: '1px solid rgba(201,168,76,0.25)', borderRadius: 14, padding: '20px 24px' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6 }}>AI Market Intelligence Report</div>
                    <div style={{ fontSize: 13, color: 'var(--text2)', maxWidth: 500, lineHeight: 1.6 }}>
                      Our AI searches live auction results, market data and collector car publications to generate a comprehensive investment report for your <strong>{selectedVehicle?.brand} {selectedVehicle?.model}</strong>.
                    </div>
                    <div style={{ display: 'flex', gap: 12, marginTop: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>🌐 Live web search</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>📊 {comparables.length} comparables</span>
                      <span style={{ fontSize: 11, color: 'var(--text3)', display: 'flex', alignItems: 'center', gap: 4 }}>⏱ ~15 seconds</span>
                    </div>
                  </div>
                  <button
                    className="btn btn-primary"
                    onClick={generateMarketIntelligence}
                    disabled={reportLoading}
                    style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c97a)', boxShadow: '0 4px 16px rgba(201,168,76,0.4)', whiteSpace: 'nowrap', flexShrink: 0 }}
                  >
                    {reportLoading
                      ? <><Loader size={14} style={{ animation: 'spin 0.8s linear infinite' }}/> Generating…</>
                      : <><Sparkles size={14}/> Generate Report</>
                    }
                  </button>
                </div>
              </div>

              {reportError && (
                <div style={{ background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.25)', borderRadius: 10, padding: '12px 16px', fontSize: 13, color: '#ef4444' }}>
                  ⚠️ {reportError}
                </div>
              )}

              {reportLoading && (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '48px 20px', textAlign: 'center' }}>
                  <div className="spinner" style={{ margin: '0 auto 16px' }}/>
                  <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 8 }}>Searching live market data…</div>
                  <div style={{ fontSize: 12, color: 'var(--text3)' }}>Analysing auction results, market trends and collector sentiment</div>
                </div>
              )}

              {marketReport && !reportLoading && (
                <>
                  {/* Appreciation Forecast */}
                  {marketReport.appreciation_forecast && (
                    <div className="card">
                      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 6 }}>Appreciation Forecast</div>
                      <div style={{ fontSize: 13, color: 'var(--text2)', marginBottom: 16 }}>{marketReport.forecast_reasoning}</div>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10 }}>
                        {[
                          { period: '1 Year', pct: marketReport.appreciation_forecast.year_1 },
                          { period: '3 Years', pct: marketReport.appreciation_forecast.year_3 },
                          { period: '5 Years', pct: marketReport.appreciation_forecast.year_5 },
                          { period: '10 Years', pct: marketReport.appreciation_forecast.year_10 },
                        ].map((item, i) => {
                          const color = item.pct >= 20 ? '#22c55e' : item.pct >= 5 ? '#4f8ef7' : item.pct >= 0 ? '#eab308' : '#ef4444';
                          const currentVal = result?.estimatedValue ? Number(result.estimatedValue) : null;
                          const projectedVal = currentVal ? currentVal * (1 + item.pct / 100) : null;
                          return (
                            <div key={i} style={{ background: `${color}0d`, border: `1px solid ${color}25`, borderRadius: 10, padding: '14px', textAlign: 'center' }}>
                              <div style={{ fontSize: 11, color: 'var(--text3)', marginBottom: 6 }}>{item.period}</div>
                              <div style={{ fontSize: 20, fontWeight: 800, color }}>{item.pct >= 0 ? '+' : ''}{item.pct}%</div>
                              {projectedVal && <div style={{ fontSize: 11, color: 'var(--text2)', marginTop: 4 }}>≈ €{Math.round(projectedVal).toLocaleString()}</div>}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {/* Price Trend Graph */}
                  {marketReport.price_trend && marketReport.price_trend.length > 1 && (() => {
                    const hist = [...marketReport.price_trend]
                      .map(p => ({ year: Number(p.year), value: Number(p.value) }))
                      .filter(p => p.year && p.value)
                      .sort((a, b) => a.year - b.year);
                    if (hist.length < 2) return null;

                    const curYear = new Date().getFullYear();
                    const curVal = Number(result?.estimatedValue) || hist[hist.length - 1].value;
                    const af = marketReport.appreciation_forecast || {};

                    // History points carry `value`; forecast points carry `forecast`.
                    // The shared point at "today" carries both so the two lines join seamlessly.
                    const data = hist.map(p => ({ year: String(p.year), value: p.value }));
                    data.push({ year: String(curYear), value: curVal, forecast: curVal });
                    [
                      { y: curYear + 1, pct: af.year_1 },
                      { y: curYear + 3, pct: af.year_3 },
                      { y: curYear + 5, pct: af.year_5 },
                      { y: curYear + 10, pct: af.year_10 },
                    ].forEach(f => data.push({ year: String(f.y), forecast: curVal * (1 + (Number(f.pct) || 0) / 100) }));

                    return (
                      <div className="card">
                        <div style={{ fontWeight: 700, fontSize: 17 }}>Price Trend</div>
                        <div style={{ fontSize: 14, color: 'var(--text2)', marginBottom: 20 }}>
                          AI-estimated market value over time · solid = historical, dashed = forecast
                        </div>
                        <ResponsiveContainer width="100%" height={200}>
                          <AreaChart data={data} margin={{ top: 5, right: 5, left: 0, bottom: 0 }}>
                            <defs>
                              <linearGradient id="trendGrad" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#4f8ef7" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#4f8ef7" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                            <XAxis dataKey="year" tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} />
                            <YAxis tick={{ fill: 'rgba(255,255,255,0.3)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `€${Math.round(v / 1000)}k`} />
                            <Tooltip content={<CustomTooltip />} />
                            <Area type="monotone" dataKey="value" stroke="#4f8ef7" strokeWidth={2.5} fill="url(#trendGrad)" dot={false} activeDot={{ r: 6, fill: '#4f8ef7', stroke: 'rgba(79,142,247,0.3)', strokeWidth: 4 }} connectNulls />
                            <Area type="monotone" dataKey="forecast" stroke="#22c55e" strokeWidth={2.5} strokeDasharray="6 5" fill="none" dot={false} activeDot={{ r: 6, fill: '#22c55e', stroke: 'rgba(34,197,94,0.3)', strokeWidth: 4 }} connectNulls />
                          </AreaChart>
                        </ResponsiveContainer>
                        <div style={{ display: 'flex', gap: 18, marginTop: 12, fontSize: 12, color: 'var(--text2)' }}>
                          <span><span style={{ display: 'inline-block', width: 14, height: 2, background: '#4f8ef7', verticalAlign: 'middle', marginRight: 6 }} />Historical (estimated)</span>
                          <span><span style={{ display: 'inline-block', width: 14, height: 0, borderTop: '2px dashed #22c55e', verticalAlign: 'middle', marginRight: 6 }} />Forecast</span>
                        </div>
                      </div>
                    );
                  })()}

                  {/* Investment Verdict */}
                  {(() => {
                    const verdictColors = { 'Strong Hold': '#22c55e', 'Hold': '#4f8ef7', 'Consider Selling': '#eab308', 'Sell': '#ef4444' }
                    const color = verdictColors[marketReport.investment_verdict] || '#4f8ef7';
                    return (
                      <div style={{ background: `${color}12`, border: `1px solid ${color}35`, borderRadius: 14, padding: '20px 24px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
                        <div>
                          <div style={{ fontSize: 11, color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>Investment Verdict</div>
                          <div style={{ fontSize: 28, fontWeight: 900, color, marginBottom: 6 }}>{marketReport.investment_verdict}</div>
                          <div style={{ fontSize: 13, color: 'var(--text2)', maxWidth: 500, lineHeight: 1.6 }}>{marketReport.verdict_reasoning}</div>
                        </div>
                        <div style={{ display: 'flex', flex: 'column', gap: 8 }}>
                          {(marketReport.key_value_drivers || []).map((d, i) => (
                            <div key={i} style={{ fontSize: 12, color: '#22c55e', display: 'flex', alignItems: 'center', gap: 6 }}>
                              <span>✓</span> {d}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })()}

                  {/* Market Timing */}
                  <div className="card">
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 16 }}>Market Timing</div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                      {[
                        { label: 'Best Season to Sell', value: marketReport.best_season_to_sell, icon: '🗓', reasoning: marketReport.best_season_reasoning, color: '#4f8ef7' },
                        { label: 'Recommended Hold', value: marketReport.recommended_holding_years ? `${marketReport.recommended_holding_years} years` : '—', icon: '⏳', reasoning: marketReport.holding_reasoning, color: '#a855f7' },
                        { label: 'Next Peak Year', value: marketReport.next_peak_year || '—', icon: '📈', reasoning: marketReport.peak_year_reasoning, color: '#22c55e' },
                      ].map((item, i) => (
                        <div key={i} style={{ background: `${item.color}0d`, border: `1px solid ${item.color}25`, borderRadius: 12, padding: '16px' }}>
                          <div style={{ fontSize: 10, color: item.color, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 1, marginBottom: 6 }}>{item.icon} {item.label}</div>
                          <div style={{ fontSize: 20, fontWeight: 800, color: item.color, marginBottom: 8 }}>{item.value}</div>
                          <div style={{ fontSize: 12, color: 'var(--text2)', lineHeight: 1.6 }}>{item.reasoning}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Market Narrative */}
                  {marketReport.market_narrative && (
                    <div className="card">
                      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 20 }}>Market Analysis</div>
                      {[
                        { title: '📊 Market Overview', content: marketReport.market_narrative.overview },
                        { title: '🔥 Demand Drivers', content: marketReport.market_narrative.demand_drivers },
                        { title: '🌍 Regional Analysis', content: marketReport.market_narrative.regional_analysis },
                        { title: '💰 Investment Outlook', content: marketReport.market_narrative.investment_outlook },
                        { title: '⚠️ Risk Factors', content: marketReport.market_narrative.risk_factors },
                      ].filter(s => s.content).map((section, i) => (
                        <div key={i} style={{ marginBottom: 20, paddingBottom: 20, borderBottom: i < 4 ? '1px solid var(--border)' : 'none' }}>
                          <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 8, color: 'var(--text)' }}>{section.title}</div>
                          <div style={{ fontSize: 14, color: 'var(--text2)', lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>{section.content}</div>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Risk Factors */}
                  {marketReport.risk_factors?.length > 0 && (
                    <div className="card">
                      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 14 }}>Risk Factors</div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {marketReport.risk_factors.map((risk, i) => (
                          <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: 10, padding: '10px 14px', background: 'rgba(239,68,68,0.06)', border: '1px solid rgba(239,68,68,0.15)', borderRadius: 8 }}>
                            <span style={{ color: '#ef4444', flexShrink: 0 }}>⚠️</span>
                            <span style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 1.6 }}>{risk}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Sources used */}
                  <div className="card" style={{ padding: '16px 20px' }}>
                    <div style={{ fontWeight: 700, fontSize: 14, marginBottom: 12 }}>Sources Consulted</div>
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 12 }}>
                      {[
                        { name: 'Hagerty', url: 'hagerty.com' },
                        { name: 'Bring a Trailer', url: 'bringatrailer.com' },
                        { name: 'Classic.com', url: 'classic.com' },
                        { name: 'RM Sotheby\'s', url: 'rmsothebys.com' },
                        { name: 'Classic & Sports Car', url: 'classicandsportscar.com' },
                        { name: 'AutoTrader Classics', url: 'autotrader.com' },
                      ].map((s, i) => (
                        <a key={i} href={`https://${s.url}`} target="_blank" rel="noreferrer" style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--accent)', textDecoration: 'none', fontFamily: 'DM Mono, monospace', transition: 'all 0.15s' }}
                          onMouseEnter={e => e.currentTarget.style.borderColor = 'var(--accent)'}
                          onMouseLeave={e => e.currentTarget.style.borderColor = 'var(--border)'}
                        >
                          {s.name}
                        </a>
                      ))}
                      <span style={{ fontSize: 11, padding: '4px 10px', borderRadius: 20, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', color: '#22c55e', fontFamily: 'DM Mono, monospace' }}>
                        + {comparables.length} saved comparables
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: 'var(--text3)', lineHeight: 1.6 }}>
                      Sources are searched live via web at time of generation. Results may vary based on available data. · Generated {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })} · Click Generate Report to refresh with latest data.
                    </div>
                  </div>

                  {/* Report History */}
                  {savedReports.length > 1 && (
                    <div className="card">
                      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 16 }}>Report History</div>
                      <div style={{ overflowX: 'auto' }}>
                        <table style={{ minWidth: 500 }}>
                          <thead>
                            <tr>
                              <th>Generated</th>
                              <th>Verdict</th>
                              <th>Peak Year</th>
                              <th>1Y Forecast</th>
                              <th>5Y Forecast</th>
                              <th>Score</th>
                              <th></th>
                            </tr>
                          </thead>
                          <tbody>
                            {savedReports.map((r, i) => {
                              const verdictColors = { 'Strong Hold': '#22c55e', 'Hold': '#4f8ef7', 'Consider Selling': '#eab308', 'Sell': '#ef4444' };
                              const color = verdictColors[r.investment_verdict] || '#4f8ef7';
                              return (
                                <tr key={r.id}>
                                  <td style={{ fontFamily: 'DM Mono, monospace', fontSize: 12 }}>
                                    {new Date(r.generated_at).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' })}
                                  </td>
                                  <td>
                                    <span style={{ fontSize: 12, padding: '2px 10px', borderRadius: 20, fontWeight: 600, background: `${color}18`, color, border: `1px solid ${color}33` }}>
                                      {r.investment_verdict || '—'}
                                    </span>
                                  </td>
                                  <td style={{ color: '#22c55e', fontWeight: 600 }}>{r.next_peak_year || '—'}</td>
                                  <td style={{ color: (r.forecast_year_1 || 0) >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                                    {r.forecast_year_1 ? `${r.forecast_year_1 >= 0 ? '+' : ''}${r.forecast_year_1}%` : '—'}
                                  </td>
                                  <td style={{ color: (r.forecast_year_5 || 0) >= 0 ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                                    {r.forecast_year_5 ? `${r.forecast_year_5 >= 0 ? '+' : ''}${r.forecast_year_5}%` : '—'}
                                  </td>
                                  <td style={{ fontWeight: 600 }}>{r.collector_score || '—'}/100</td>
                                  <td>
                                    <div style={{ display: 'flex', gap: 6 }}>
                                      <button
                                        className="btn btn-ghost btn-sm"
                                        onClick={() => setMarketReport({
                                          investment_verdict: r.investment_verdict,
                                          verdict_reasoning: r.verdict_reasoning,
                                          key_value_drivers: r.key_value_drivers,
                                          best_season_to_sell: r.best_season_to_sell,
                                          best_season_reasoning: r.best_season_reasoning,
                                          recommended_holding_years: r.recommended_holding_years,
                                          holding_reasoning: r.holding_reasoning,
                                          next_peak_year: r.next_peak_year,
                                          peak_year_reasoning: r.peak_year_reasoning,
                                          appreciation_forecast: { year_1: r.forecast_year_1, year_3: r.forecast_year_3, year_5: r.forecast_year_5, year_10: r.forecast_year_10 },
                                          forecast_reasoning: r.forecast_reasoning,
                                          price_trend: r.price_trend,
                                          market_narrative: { overview: r.narrative_overview, demand_drivers: r.narrative_demand_drivers, regional_analysis: r.narrative_regional, investment_outlook: r.narrative_investment_outlook, risk_factors: r.narrative_risk_factors },
                                          risk_factors: r.risk_factors,
                                        })}
                                      >
                                        View
                                      </button>
                                      <button
                                        className="btn btn-danger btn-sm"
                                        onClick={async () => {
                                          if (!window.confirm('Delete this report?')) return;
                                          await supabase.from('market_intelligence_reports').delete().eq('id', r.id);
                                          fetchSavedReports();
                                          toast.success('Report deleted');
                                        }}
                                      >
                                        <Trash2 size={11}/>
                                      </button>
                                    </div>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </>
              )}

              {!marketReport && !reportLoading && !reportError && (
                <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '48px 20px', textAlign: 'center' }}>
                  <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(201,168,76,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
                    <TrendingUp size={28} style={{ color: '#c9a84c' }} />
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>No report generated yet</div>
                  <div style={{ fontSize: 13, color: 'var(--text2)', maxWidth: 360, margin: '0 auto' }}>
                    Click "Generate Report" above to create an AI-powered market intelligence report with live data for your {selectedVehicle?.brand} {selectedVehicle?.model}.
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="card" style={{ textAlign: 'center', padding: '48px 24px' }}>
              <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(201,168,76,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 18px' }}>
                <Lock size={26} style={{ color: '#c9a84c' }} />
              </div>
              <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 8 }}>Market Intelligence is a Curator feature</div>
              <div style={{ fontSize: 13.5, color: 'var(--text2)', maxWidth: 420, margin: '0 auto 20px', lineHeight: 1.6 }}>
                AI-generated appreciation forecasts, market narrative and investment verdicts are available on the <strong style={{ color: '#c9a84c' }}>Curator</strong> plan.
              </div>
              {!isNative && (
                <button className="btn btn-primary" style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c97a)' }} onClick={() => navigate('/billing')}>
                  Upgrade to Curator →
                </button>
              )}
            </div>
          ))}

          {/* ── Description Tab ── */}
          {activeTab === 'description' && (
            <VehicleDescriptionTab vehicle={selectedVehicle} onSaved={(desc) => setVehicles(prev => prev.map(v => v.id === selectedVehicleId ? { ...v, description: desc } : v))} />
          )}

          {/* ── Reports Tab ── */}
          {activeTab === 'reports' && (
            <div>
              {reports.filter(r => r.vehicle_id === selectedVehicleId).length === 0 ? (
                <div className="card" style={{ textAlign: 'center', padding: '48px 20px' }}>
                  <Save size={36} style={{ color: 'var(--text3)', margin: '0 auto 12px', opacity: 0.3 }}/>
                  <h3 style={{ fontWeight: 600, marginBottom: 6 }}>No saved reports yet</h3>
                  <p style={{ color: 'var(--text3)', fontSize: 13 }}>Save a valuation from the Collector Score tab to start building a history.</p>
                  <button className="btn btn-primary btn-sm" style={{ marginTop: 12 }} onClick={() => setActiveTab('collector')}>Go to Collector Score</button>
                </div>
              ) : (
                <>
                  {reportChartData.length >= 2 && (
                    <div className="card" style={{ marginBottom: 16 }}>
                      <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 16 }}>Valuation History Chart</div>
                      <ResponsiveContainer width="100%" height={220}>
                        <AreaChart data={reportChartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                          <defs>
                            <linearGradient id="repGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="5%" stopColor="#22c55e" stopOpacity={0.3}/>
                              <stop offset="95%" stopColor="#22c55e" stopOpacity={0}/>
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" vertical={false}/>
                          <XAxis dataKey="date" tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false}/>
                          <YAxis tick={{ fill: 'var(--text3)', fontSize: 11 }} axisLine={false} tickLine={false} tickFormatter={v => `€${v}`}/>
                          <Tooltip content={<CustomTooltip/>}/>
                          <Area type="monotone" dataKey="value" stroke="#22c55e" strokeWidth={3} fill="url(#repGrad)" dot={{ fill: '#22c55e', r: 5 }} activeDot={{ r: 8 }}/>
                        </AreaChart>
                      </ResponsiveContainer>
                    </div>
                  )}
                  <div className="card">
                    <div style={{ fontWeight: 700, fontSize: 17, marginBottom: 16 }}>Saved Reports</div>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ minWidth: 600 }}>
                        <thead><tr><th>Date</th><th>Median Comparable</th><th>Estimated Value</th><th>Purchase</th><th>Gain/Loss</th><th>Confidence</th><th></th></tr></thead>
                        <tbody>
                          {reports.filter(r => r.vehicle_id === selectedVehicleId).map(r => (
                            <tr key={r.id}>
                              <td>{formatDate(r.report_date)}</td>
                              <td>{formatCurrency(r.avg_comparable_price)}</td>
                              <td style={{ fontWeight: 700, color: 'var(--accent)' }}>{formatCurrency(r.estimated_value)}</td>
                              <td>{formatCurrency(r.purchase_price)}</td>
                              <td style={{ fontWeight: 700, color: (r.appreciation_amount || 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>
                                {r.appreciation_amount !== null ? `${r.appreciation_amount >= 0 ? '+' : ''}${formatCurrency(Math.abs(r.appreciation_amount))}` : '—'}
                                {r.appreciation_pct !== null ? ` (${r.appreciation_pct >= 0 ? '+' : ''}${Number(r.appreciation_pct).toFixed(1)}%)` : ''}
                              </td>
                              <td>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                  <div style={{ height: 4, width: 50, background: 'var(--surface2)', borderRadius: 2, overflow: 'hidden' }}>
                                    <div style={{ height: '100%', width: `${r.confidence_score || 0}%`, background: (r.confidence_score || 0) >= 70 ? '#22c55e' : '#4f8ef7', borderRadius: 2 }}/>
                                  </div>
                                  <span style={{ fontSize: 11 }}>{r.confidence_score}%</span>
                                </div>
                              </td>
                              <td>
                                <button className="btn btn-danger btn-sm" title="Delete report" onClick={async () => {
                                  if (!window.confirm('Delete this saved report? This cannot be undone.')) return;
                                  const { error } = await supabase.from('valuation_reports').delete().eq('id', r.id);
                                  if (error) { toast.error('Could not delete: ' + error.message); return; }
                                  toast.success('Report deleted'); fetchData();
                                }}>✕</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </>
              )}
            </div>
          )}
        </>
      )}

      {/* Modals */}
      {showValuationModal && (() => {
        // Auto-pull restoration cost/year from this vehicle's own "Restoration"
        // category expenses (the same records shown on the vehicle's Restoration
        // tab), so the owner doesn't have to re-type numbers already on file.
        const restExp = expenses.filter(e => e.vehicle_id === selectedVehicleId && e.category === 'Restoration');
        const restorationSpend = restExp.length ? restExp.reduce((s, e) => s + Number(e.amount || 0), 0) : null;
        const restYears = restExp.map(e => e.date ? new Date(e.date).getFullYear() : null).filter(Boolean);
        const restorationYear = restYears.length ? Math.max(...restYears) : null;
        return (
          <ValuationModal
            vehicleId={selectedVehicleId}
            record={editValuation}
            vehicle={selectedVehicle}
            restorationSpend={restorationSpend}
            restorationYear={restorationYear}
            onClose={() => { setShowValuationModal(false); setEditValuation(null); fetchData(); }}
          />
        );
      })()}
      {showHistoryModal  && <HistoryModal vehicleId={selectedVehicleId} onClose={() => { setShowHistoryModal(false); fetchData(); }}/>}
    </div>
  );
};

// ── Valuation Modal ───────────────────────────────────────────────────────────
// Condense the stored Gemini Market Intelligence report into the report's text summary.
// (Previously this saved `aiAnalysis`, whose only UI was removed with the dead calculator tab,
// so every saved report stored an empty string.)
const buildMarketSummary = (r) => {
  if (!r) return null;
  const af = r.appreciation_forecast || {};
  const parts = [
    r.investment_verdict ? `Verdict: ${r.investment_verdict}` : null,
    r.verdict_reasoning || null,
    (af.year_1 != null || af.year_5 != null)
      ? `Forecast — 1yr ${af.year_1 ?? '—'}%, 3yr ${af.year_3 ?? '—'}%, 5yr ${af.year_5 ?? '—'}%, 10yr ${af.year_10 ?? '—'}%`
      : null,
    (r.key_value_drivers || []).length ? `Key value drivers: ${r.key_value_drivers.join('; ')}` : null,
    r.recommended_holding_years ? `Recommended hold: ${r.recommended_holding_years} years` : null,
    r.next_peak_year ? `Next peak year: ${r.next_peak_year}` : null,
  ].filter(Boolean);
  return parts.length ? parts.join('\n\n') : null;
};

// ── Valuation form primitives (module scope: stable identity across renders) ──
// The whole form is CONTROLLED — a single `form` state object. This is what fixes
// the data-loss bug: collapsing a FormSection unmounts its inputs, and uncontrolled
// inputs (defaultValue + ref) kept their value only in the DOM, so it vanished.
// With state in the parent, unmounting a section loses nothing.

const VF_Num = ({ form, set, field, label, placeholder, suffix }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <div style={{ position: 'relative' }}>
      <input className="form-input" type="number" step="any" value={form[field] ?? ''}
        onChange={e => set(field, e.target.value)} placeholder={placeholder}
        style={suffix ? { paddingRight: 42 } : undefined}/>
      {suffix && <span style={{ position: 'absolute', right: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: 'var(--text3)' }}>{suffix}</span>}
    </div>
  </div>
);

const VF_Text = ({ form, set, field, label, placeholder }) => (
  <div className="form-group">
    <label className="form-label">{label}</label>
    <input className="form-input" value={form[field] ?? ''} onChange={e => set(field, e.target.value)} placeholder={placeholder}/>
  </div>
);

const VF_Area = ({ form, set, field, label, placeholder, minHeight = 80 }) => (
  <div className="form-group">
    {label && <label className="form-label">{label}</label>}
    <textarea className="form-input" value={form[field] ?? ''} onChange={e => set(field, e.target.value)}
      placeholder={placeholder} style={{ minHeight }}/>
  </div>
);

// Ordinal grade -> slider. `options` run worst → best so left/right reads naturally.
const VF_Slider = ({ form, set, field, label, options, hint }) => {
  const idx = Math.max(0, options.indexOf(form[field]));
  const pct = options.length > 1 ? (idx / (options.length - 1)) * 100 : 0;
  const tone = pct >= 75 ? '#22c55e' : pct >= 50 ? '#4f8ef7' : pct >= 25 ? '#eab308' : '#f97316';
  return (
    <div className="form-group">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 6 }}>
        <label className="form-label" style={{ margin: 0 }}>{label}</label>
        <span style={{ fontSize: 13, fontWeight: 700, color: tone }}>{options[idx]}</span>
      </div>
      <input type="range" min={0} max={options.length - 1} step={1} value={idx}
        onChange={e => set(field, options[Number(e.target.value)])}
        style={{ width: '100%', accentColor: tone, cursor: 'pointer' }}/>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--text3)', marginTop: 2 }}>
        <span>{options[0]}</span><span>{options[options.length - 1]}</span>
      </div>
      {hint && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>{hint}</div>}
    </div>
  );
};

// Boolean -> tappable chip. Much faster to scan than a column of checkboxes.
const VF_Chip = ({ form, set, field, label }) => {
  const on = !!form[field];
  return (
    <button type="button" onClick={() => set(field, !on)}
      style={{
        display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
        padding: '10px 12px', borderRadius: 10, cursor: 'pointer', fontSize: 13,
        background: on ? 'rgba(34,197,94,0.12)' : 'var(--surface2)',
        border: `1px solid ${on ? 'rgba(34,197,94,0.45)' : 'var(--border)'}`,
        color: on ? '#22c55e' : 'var(--text2)',
        fontWeight: on ? 600 : 400, transition: 'var(--transition)',
      }}>
      <span style={{
        width: 16, height: 16, borderRadius: 5, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: on ? '#22c55e' : 'transparent',
        border: `1px solid ${on ? '#22c55e' : 'var(--text3)'}`,
        color: '#fff', fontSize: 11, lineHeight: 1,
      }}>{on ? '✓' : ''}</span>
      {label}
    </button>
  );
};

const ValuationModal = ({ vehicleId, record, vehicle, restorationSpend, restorationYear, onClose }) => {
  const [saving, setSaving] = useState(false);

  const TEXT_FIELDS = ['purchase_date','condition_grade','factory_color_rarity','transmission_type',
    'restoration_status','market_trend','category','service_history_status','provenance_notes','notes',
    'vin','factory_order_number','build_date','original_colour_name','colour_desirability',
    'interior_colour','interior_material','modifications_list','modification_impact','best_season_to_sell'];
  const NUM_FIELDS = ['purchase_price','current_value','manual_market_value','insurance_agreed_value','lowest_sale','average_sale',
    'highest_sale','private_market_estimate','mileage_score','total_production','remaining_units',
    'restoration_year','restoration_cost','category_multiplier','special_edition_multiplier','yearly_insurance','yearly_maintenance',
    'yearly_storage','yearly_fuel','yearly_registration','yearly_other','recommended_holding_years','next_peak_year'];
  const BOOL_FIELDS = ['original_paint','matching_numbers','original_interior','oem_parts','factory_wheels',
    'original_radio','no_modifications','specialist_servicing','major_restoration_docs','original_manuals',
    'special_edition','celebrity_owned','single_owner','museum_history','matching_vin_docs',
    'professional_restoration','frame_off_restoration'];

  const [form, setForm] = useState(() => {
    const f = {};
    TEXT_FIELDS.forEach(k => { f[k] = record?.[k] ?? ''; });
    NUM_FIELDS.forEach(k  => { f[k] = record?.[k] ?? ''; });
    BOOL_FIELDS.forEach(k => { f[k] = record?.[k] ?? false; });
    // sensible defaults so the sliders start somewhere meaningful
    f.condition_grade       = record?.condition_grade       || 'Good';
    f.service_history_status= record?.service_history_status|| 'Partial';
    f.restoration_status    = record?.restoration_status    || 'Original Survivor';
    f.colour_desirability   = record?.colour_desirability   || 'Neutral';
    f.modification_impact   = record?.modification_impact   || 'Neutral';
    f.factory_color_rarity  = record?.factory_color_rarity  || 'Common';
    // Auto-pull from the vehicle's own data / expense records when not already
    // recorded here — never overwrites something the owner already entered.
    if (!f.vin && vehicle?.vin) f.vin = vehicle.vin;
    if ((f.restoration_cost === '' || f.restoration_cost == null) && restorationSpend) f.restoration_cost = restorationSpend;
    if ((f.restoration_year === '' || f.restoration_year == null) && restorationYear) f.restoration_year = restorationYear;
    return f;
  });
  const vinAutoFilled = !record?.vin && !!vehicle?.vin;
  const restCostAutoFilled = !record?.restoration_cost && !!restorationSpend;
  const restYearAutoFilled = !record?.restoration_year && !!restorationYear;
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  // Live preview — the real scoring function, so the modal can't drift from the page.
  const preview = calcCollectorScore({
    ...form,
    total_production: form.total_production === '' ? null : Number(form.total_production),
    remaining_units: form.remaining_units === '' ? null : Number(form.remaining_units),
    special_edition_multiplier: form.special_edition_multiplier === '' ? null : Number(form.special_edition_multiplier),
  });

  const submit = async (e) => {
    e.preventDefault(); setSaving(true);
    const payload = { vehicle_id: vehicleId };
    TEXT_FIELDS.forEach(k => { payload[k] = form[k] === '' ? null : form[k]; });
    NUM_FIELDS.forEach(k  => { payload[k] = form[k] === '' || form[k] == null ? null : parseFloat(form[k]); });
    BOOL_FIELDS.forEach(k => { payload[k] = !!form[k]; });

    let error;
    if (record) ({ error } = await supabase.from('vehicle_valuations').update({ ...payload, updated_at: new Date().toISOString() }).eq('id', record.id));
    else ({ error } = await supabase.from('vehicle_valuations').insert(payload).select());
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    toast.success(record ? 'Updated!' : 'Saved!'); onClose(); setSaving(false);
  };

  const scoreTone = preview.total >= 70 ? '#22c55e' : preview.total >= 50 ? '#4f8ef7' : '#eab308';

  return (
    <Modal title={record ? 'Edit Vehicle Data' : 'Add Vehicle Data'} onClose={onClose} size="lg" hideClose
      footer={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="val-form" type="submit" disabled={saving}>{saving ? 'Saving…' : record ? 'Update' : 'Save'}</button></>}>

      {/* Live score preview — updates as you move each slider */}
      <div style={{ position: 'sticky', top: 0, zIndex: 5, background: 'var(--surface)', borderBottom: '1px solid var(--border)', padding: '18px 6px 20px', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
          <div style={{ position: 'relative', width: 104, height: 104, flexShrink: 0 }}>
            <svg width="104" height="104" style={{ transform: 'rotate(-90deg)' }}>
              <circle cx="52" cy="52" r="44" fill="none" stroke="var(--border)" strokeWidth="9"/>
              <circle cx="52" cy="52" r="44" fill="none" stroke={scoreTone} strokeWidth="9" strokeLinecap="round"
                strokeDasharray={`${(preview.total / 100) * 276.5} 276.5`} style={{ transition: 'stroke-dasharray 0.35s ease, stroke 0.35s ease' }}/>
            </svg>
            <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
              <span style={{ fontSize: 34, fontWeight: 800, color: scoreTone, lineHeight: 1, letterSpacing: -1 }}>{preview.total}</span>
              <span style={{ fontSize: 11, color: 'var(--text3)', marginTop: 2 }}>/ 100</span>
            </div>
          </div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 16, fontWeight: 700, marginBottom: 12 }}>Collector Score — live</div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '10px 16px' }}>
              {Object.entries(preview.breakdown || {}).map(([name, b]) => {
                const p = b.max > 0 ? (b.score / b.max) * 100 : 0;
                return (
                  <div key={name} title={`${name}: ${b.score}/${b.max}${b.recorded ? '' : ' (not recorded — counted as average)'}`}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', fontSize: 12, color: 'var(--text2)', marginBottom: 4 }}>
                      <span style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{name}</span>
                      <span style={{ fontWeight: 700, color: b.recorded ? 'var(--text)' : 'var(--text3)', opacity: b.recorded ? 1 : 0.6, flexShrink: 0, marginLeft: 8 }}>{b.score}/{b.max}</span>
                    </div>
                    <div style={{ height: 7, background: 'var(--surface2)', borderRadius: 4, overflow: 'hidden' }}>
                      <div style={{ height: '100%', width: `${p}%`, background: b.recorded ? scoreTone : 'var(--text3)', opacity: b.recorded ? 1 : 0.4, borderRadius: 4, transition: 'width 0.35s ease, background 0.35s ease' }}/>
                    </div>
                  </div>
                );
              })}
            </div>
            <div style={{ fontSize: 12, color: preview.completeness === 100 ? 'var(--text3)' : '#f97316', marginTop: 12 }}>
              {preview.completeness}% of categories recorded{preview.completeness < 100 ? ' — blanks count as average, not zero' : ''}
            </div>
          </div>
        </div>
      </div>

      <form id="val-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
        <FormSection title="📊 Purchase & Current Value">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <VF_Num form={form} set={set} field="purchase_price" label="Purchase Price (€)"/>
              <div className="form-group"><label className="form-label">Purchase Date</label>
                <input className="form-input" type="date" value={form.purchase_date ?? ''} onChange={e => set('purchase_date', e.target.value)}/></div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <VF_Num form={form} set={set} field="insurance_agreed_value" label="Insurance Agreed Value (€)"/>
            </div>
            <div>
              <VF_Num form={form} set={set} field="manual_market_value" label="Your Own Market Estimate (€)"/>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6, marginBottom: 10, lineHeight: 1.5 }}>
                Used only when fewer than 3 comparables match this vehicle. A comparables-backed
                estimate always takes precedence, and this figure is labelled as your own.
              </div>
              <MileageInput label="Current Mileage" defaultUnit="mi"
                value={form.mileage_score} onChange={v => set('mileage_score', v)}/>
              <MileageMismatchNotice thisKm={form.mileage_score} otherKm={vehicle?.mileage}
                thisLabel="Current Mileage here" otherLabel="the vehicle’s Edit Vehicle card" />
            </div>
          </div>
        </FormSection>

        <FormSection title="🏁 Condition" defaultOpen={false}>
          <VF_Slider form={form} set={set} field="condition_grade" label="Overall Condition"
            options={['Poor','Fair','Good','Excellent','Concours']}
            hint="Biggest single driver of estimated value — Concours ×1.30 down to Poor ×0.60."/>
        </FormSection>

        <FormSection title="⭐ Originality" defaultOpen={false}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            <VF_Chip form={form} set={set} field="original_paint" label="Original Paint"/>
            <VF_Chip form={form} set={set} field="matching_numbers" label="Matching Numbers Engine"/>
            <VF_Chip form={form} set={set} field="original_interior" label="Original Interior"/>
            <VF_Chip form={form} set={set} field="oem_parts" label="OEM Parts"/>
            <VF_Chip form={form} set={set} field="factory_wheels" label="Factory Wheels"/>
            <VF_Chip form={form} set={set} field="original_radio" label="Original Radio"/>
            <VF_Chip form={form} set={set} field="no_modifications" label="No Modifications"/>
          </div>
        </FormSection>

        <FormSection title="📋 Service History" defaultOpen={false}>
          <VF_Slider form={form} set={set} field="service_history_status" label="Service History"
            options={['None','Partial','Complete']}/>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8, marginTop: 12 }}>
            <VF_Chip form={form} set={set} field="specialist_servicing" label="Specialist Servicing"/>
            <VF_Chip form={form} set={set} field="original_manuals" label="Original Manuals"/>
          </div>
        </FormSection>

        <FormSection title="💎 Rarity" defaultOpen={false}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <VF_Num form={form} set={set} field="total_production" label="Total Units Produced"/>
              <VF_Num form={form} set={set} field="remaining_units" label="Estimated Remaining"/>
            </div>
            <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.5 }}>
              Drives the Supply multiplier: ≤50 remaining = 1.5x, 51–100 = 1.3x, 101–250 = 1.15x,
              251–1000 = 1.05x — applied directly to both the Collector Score and the estimated value.
              Falls back to Total Units Produced when Estimated Remaining is blank.
            </div>
            <VF_Slider form={form} set={set} field="factory_color_rarity" label="Factory Colour Rarity"
              options={['Common','Uncommon','Rare','Extremely Rare']}/>
            <div className="form-group">
              <label className="form-label">Transmission</label>
              <select className="form-input" value={form.transmission_type ?? ''} onChange={e => set('transmission_type', e.target.value)}>
                <option value="">—</option>
                {['Manual','Automatic','Both Available'].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
              <VF_Chip form={form} set={set} field="special_edition" label="Special / Limited Edition"/>
            </div>
            {form.special_edition && (
              <div>
                <VF_Num form={form} set={set} field="special_edition_multiplier" label="Special Edition Multiplier"/>
                <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.5 }}>
                  Applied directly to the Collector Score and estimated value. Leave blank to use the
                  default of 1.5x — enter a higher number for something rarer (e.g. a numbered anniversary edition).
                </div>
              </div>
            )}
            <div>
              <VF_Num form={form} set={set} field="category_multiplier" label="Category / Segment Multiplier (override)"/>
              <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.5 }}>
                Optional manual multiplier for this car's whole segment (e.g. air-cooled Porsche market
                premium = 1.2). Applied on top of every other factor. Leave blank for 1.0x (no effect).
              </div>
            </div>
          </div>
        </FormSection>

        <FormSection title="🏆 Provenance" defaultOpen={false}>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
            <VF_Chip form={form} set={set} field="single_owner" label="Single Owner"/>
            <VF_Chip form={form} set={set} field="matching_vin_docs" label="Matching VIN Docs"/>
            <VF_Chip form={form} set={set} field="museum_history" label="Museum History"/>
            <VF_Chip form={form} set={set} field="celebrity_owned" label="Celebrity Owned"/>
          </div>
          <div style={{ marginTop: 12 }}>
            <VF_Text form={form} set={set} field="vin" label="VIN / Chassis Number" placeholder="Adds to the provenance score"/>
            {vinAutoFilled && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Auto-filled from this vehicle's record — edit if needed.</div>}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginTop: 12 }}>
            <VF_Text form={form} set={set} field="factory_order_number" label="Factory Order Number"/>
            <div className="form-group"><label className="form-label">Build Date</label>
              <input className="form-input" type="date" value={form.build_date ?? ''} onChange={e => set('build_date', e.target.value)}/></div>
          </div>
          <VF_Area form={form} set={set} field="provenance_notes" label="Provenance Notes"/>
        </FormSection>

        <FormSection title="🔧 Restoration" defaultOpen={false}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <VF_Slider form={form} set={set} field="restoration_status" label="Restoration Status"
              options={['Project','Partially Restored','Restored','Original Survivor']}
              hint="An unrestored original survivor scores highest."/>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <VF_Num form={form} set={set} field="restoration_year" label="Restoration Year"/>
                {restYearAutoFilled && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Auto-filled from your Restoration expense records.</div>}
              </div>
              <div>
                <VF_Num form={form} set={set} field="restoration_cost" label="Restoration Cost (€)"/>
                {restCostAutoFilled && <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Auto-filled from your Restoration expense records.</div>}
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 8 }}>
              <VF_Chip form={form} set={set} field="professional_restoration" label="Professional Restoration"/>
              <VF_Chip form={form} set={set} field="frame_off_restoration" label="Frame-Off Restoration"/>
              <VF_Chip form={form} set={set} field="major_restoration_docs" label="Restoration Docs"/>
            </div>
          </div>
        </FormSection>

        <FormSection title="🎨 Colour & Interior" defaultOpen={false}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <VF_Text form={form} set={set} field="original_colour_name" label="Original Factory Colour" placeholder="e.g. Guards Red"/>
              <VF_Text form={form} set={set} field="interior_colour" label="Interior Colour" placeholder="e.g. Black, Cognac"/>
            </div>
            <VF_Slider form={form} set={set} field="colour_desirability" label="Colour Desirability"
              options={['Less Desirable','Neutral','Desirable','Highly Desirable']}/>
            <VF_Text form={form} set={set} field="interior_material" label="Interior Material" placeholder="e.g. Full leather, Alcantara"/>
          </div>
        </FormSection>

        <FormSection title="🔧 Modifications" defaultOpen={false}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <VF_Slider form={form} set={set} field="modification_impact" label="Impact on Value"
              options={['Heavily Modified','Value Negative','Neutral','Value Positive']}
              hint="Applied as a multiplier to the whole score, and to the estimated value."/>
            <VF_Area form={form} set={set} field="modifications_list" label="Modifications List"
              placeholder="e.g. Aftermarket exhaust, lowered suspension…"/>
          </div>
        </FormSection>

        <FormSection title="📝 Notes" defaultOpen={false}>
          <VF_Area form={form} set={set} field="notes" placeholder="Additional notes..."/>
        </FormSection>
      </form>
    </Modal>
  );
};

// ── History Modal ─────────────────────────────────────────────────────────────
const HistoryModal = ({ vehicleId, onClose }) => {
  const SOURCES = ['Personal Estimate','Dealer Appraisal','Auction Result','Insurance Valuation','Hagerty','Bring a Trailer','Classic.com','Other'];
  const [form, setForm] = useState({ valuation_date: format(new Date(), 'yyyy-MM-dd'), value: '', source: 'Personal Estimate', notes: '' });
  const [saving, setSaving] = useState(false);
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); if (!form.value) { toast.error('Please enter a value'); return; } setSaving(true);
    const { error } = await supabase.from('valuation_history').insert({ vehicle_id: vehicleId, valuation_date: form.valuation_date, value: parseFloat(form.value), source: form.source || null, notes: form.notes || null }).select();
    if (error) { toast.error('Error: ' + error.message); setSaving(false); return; }
    toast.success('Value logged!'); onClose(); setSaving(false);
  };
  return (
    <Modal title="Log Value Entry" onClose={onClose} footer={<><button className="btn btn-ghost" onClick={onClose}>Cancel</button><button className="btn btn-primary" form="hist-form" type="submit" disabled={saving}>{saving ? 'Saving…' : 'Log Entry'}</button></>}>
      <form id="hist-form" onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <div className="form-grid">
          <div className="form-group"><label className="form-label">Date *</label><input className="form-input" type="date" required value={form.valuation_date} onChange={e => set('valuation_date', e.target.value)}/></div>
          <div className="form-group"><label className="form-label">Estimated Value (€) *</label><input className="form-input" type="number" step="0.01" required value={form.value} onChange={e => set('value', e.target.value)} placeholder="32000"/></div>
          <div className="form-group"><label className="form-label">Source</label><select className="form-input" value={form.source} onChange={e => set('source', e.target.value)}>{SOURCES.map(s => <option key={s}>{s}</option>)}</select></div>
        </div>
        <div className="form-group"><label className="form-label">Notes</label><textarea className="form-input" value={form.notes} onChange={e => set('notes', e.target.value)}/></div>
      </form>
    </Modal>
  );
};

const detectCurrency = (url) => {
  if (!url) return null;
  if (url.includes('bringatrailer') || url.includes('carsandbids') || url.includes('hemmings') || url.includes('ebay.com')) return 'USD';
  if (url.includes('ebay.co.uk') || url.includes('classiccarsales.co.uk') || url.includes('collectingcars')) return 'GBP';
  if (url.includes('autoscout24') || url.includes('mobile.de') || url.includes('leboncoin') || url.includes('bazaraki')) return 'EUR';
  if (url.includes('autotrader.ca') || url.includes('kijiji.ca')) return 'CAD';
  if (url.includes('shannons') || url.includes('carsales.com.au') || url.includes('gumtree.com.au')) return 'AUD';
  if (url.includes('dubizzle') || url.includes('opensooq') || url.includes('yallamotor')) return 'AED';
  return null;
};